<template>
  <div style="max-width: 90%; margin: auto" class="py-10">
    <!-- Loading Overlay -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-card">
        <v-progress-circular indeterminate size="48" width="3" color="#0a1628" />
        <div class="loading-text mt-4">Section wird geladen…</div>
      </div>
    </div>

    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-8 flex-wrap" style="gap: 16px">
      <div>
        <p class="page-subtitle mb-1">Section</p>
        <div class="d-flex align-center" style="gap: 10px">
          <h1 class="page-title mb-0">{{ section.title || 'Section verwalten' }}</h1>
          <div
            v-if="githubStatus.connected && githubStatus.repo && totalSyncableFiles > 0"
            class="sync-overall"
            :class="`overall-${overallSyncStatus}`"
            :title="overallSyncTitle"
          >
            <v-icon size="14">{{ overallIcon }}</v-icon>
            <span>{{ overallLabel }}</span>
          </div>
        </div>
        <div v-if="section.handle" class="page-handle mt-2">{{ section.handle }}</div>
      </div>
      <div class="d-flex flex-wrap" style="gap: 10px">
        <button class="secondary-btn" @click="$router.push('/sections')">
          <v-icon size="16">mdi-arrow-left</v-icon>
          <span>Zurück</span>
        </button>
        <button class="secondary-btn" :disabled="!prevSectionId" @click="goToSection(prevSectionId)">
          <v-icon size="16">mdi-chevron-left</v-icon>
          <span>Vorige</span>
        </button>
        <button class="secondary-btn" :disabled="!nextSectionId" @click="goToSection(nextSectionId)">
          <span>Nächste</span>
          <v-icon size="16">mdi-chevron-right</v-icon>
        </button>
        <button
          v-if="githubStatus.connected && githubStatus.repo && totalSyncableFiles > 0"
          class="submit-btn btn-sync-all"
          :disabled="bulkSyncing || overallSyncStatus === 'all-synced'"
          @click="bulkSyncConfirm"
        >
          <v-progress-circular v-if="bulkSyncing" indeterminate size="14" width="2" color="white" />
          <v-icon v-else size="15">mdi-sync</v-icon>
          <span>{{ bulkSyncing ? `${bulkSyncProgress.current}/${bulkSyncProgress.total}…` : 'Synchronisieren' }}</span>
        </button>
        <div v-if="$can('sections.delete')" class="more-menu-wrap" ref="moreMenuRef">
          <button
            class="secondary-btn more-btn"
            :class="{ 'more-btn-open': moreMenuOpen }"
            @click="moreMenuOpen = !moreMenuOpen"
            title="Weitere Aktionen"
          >
            <v-icon size="18">mdi-dots-vertical</v-icon>
          </button>
          <div v-if="moreMenuOpen" class="more-menu">
            <button
              class="more-menu-item more-menu-item-danger"
              @click="moreMenuOpen = false; openDeleteDialog()"
            >
              <v-icon size="15" color="#D32F2F">mdi-delete-outline</v-icon>
              <span>Section löschen</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!loading" class="grid-split">
      <!-- ===================== LINKE SPALTE ===================== -->
      <div class="col-left">
        <!-- Basic Settings -->
        <div class="card-modern">
          <h2 class="section-title mb-5">Grundeinstellungen</h2>

          <div class="fg mb-4">
            <label class="fl">Titel</label>
            <div class="iw" :class="{ 'iw-f': focusField === 'title' }">
              <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-format-title</v-icon>
              <input
                v-model="section.title"
                class="ii"
                placeholder="z.B. Hero Section"
                @focus="focusField = 'title'"
                @blur="focusField = null"
              />
            </div>
          </div>

          <div class="fg mb-4">
            <label class="fl">Handle <span class="fl-hint">(nicht änderbar)</span></label>
            <div class="iw iw-readonly">
              <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-link-variant</v-icon>
              <input v-model="section.handle" class="ii" readonly />
            </div>
          </div>

          <div class="fg mb-4">
            <label class="fl">Preis</label>
            <div class="d-flex" style="gap: 12px; align-items: stretch">
              <div class="iw" :class="{ 'iw-f': focusField === 'price' }" style="flex: 1">
                <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-currency-eur</v-icon>
                <input
                  v-model.number="section.price"
                  type="number"
                  min="0"
                  step="0.01"
                  class="ii"
                  placeholder="0.00"
                  @focus="focusField = 'price'"
                  @blur="focusField = null"
                />
              </div>
              <span class="price-badge" :class="isPremium ? 'badge-premium' : 'badge-free'">
                <v-icon size="13">{{ isPremium ? 'mdi-star' : 'mdi-gift-outline' }}</v-icon>
                {{ isPremium ? 'Premium' : 'Kostenlos' }}
              </span>
            </div>
          </div>

          <div class="fg mb-4">
            <label class="fl">Demo Store Link</label>
            <div class="iw" :class="{ 'iw-f': focusField === 'demo' }">
              <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-open-in-new</v-icon>
              <input
                v-model="section.demoStore"
                class="ii"
                placeholder="https://…"
                @focus="focusField = 'demo'"
                @blur="focusField = null"
              />
            </div>
          </div>

          <!-- Custom Category Dropdown -->
          <div class="fg" ref="categoryDropdown">
            <label class="fl">Kategorie</label>
            <button
              type="button"
              class="iw iw-trigger"
              :class="{ 'iw-f': categoryMenuOpen }"
              @click="categoryMenuOpen = !categoryMenuOpen"
            >
              <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-tag-outline</v-icon>
              <span class="ii-val">{{ currentCategoryName }}</span>
              <v-progress-circular v-if="loadingCategories" indeterminate size="14" width="2" color="#888" />
              <v-icon v-else size="14" color="#888" :class="{ 'chev-open': categoryMenuOpen }">mdi-chevron-down</v-icon>
            </button>
            <div v-if="categoryMenuOpen" class="combo-menu">
              <button
                v-for="cat in categories"
                :key="cat.id"
                type="button"
                class="combo-option"
                @click="selectCategory(cat)"
              >
                <span class="combo-label">{{ cat.name }}</span>
                <v-icon v-if="section.categoryId === cat.id" size="14" color="#2E7D32" style="margin-left: auto">mdi-check</v-icon>
              </button>
              <div v-if="categories.length === 0" class="combo-empty">Keine Kategorien</div>
            </div>
          </div>

          <div v-if="saveMessage" class="mt-4" :class="saveSuccess ? 'alert-success' : 'alert-error'">
            <v-icon size="16" :color="saveSuccess ? '#2E7D32' : '#D32F2F'">
              {{ saveSuccess ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}
            </v-icon>
            {{ saveMessage }}
          </div>
        </div>

        <!-- Files Grid -->
        <div class="files-grid">
        <!-- Liquid Code -->
        <div class="card-modern">
          <h2 class="section-title mb-4">
            <div class="title-icon" style="background: rgba(46,125,50,0.1)"><v-icon size="16" color="#2E7D32">mdi-code-braces</v-icon></div>
            Liquid Code
          </h2>

          <input
            ref="liquidFileInput"
            type="file"
            accept=".liquid"
            style="display: none"
            @change="handleLiquidFileSelect"
          />

          <div v-if="loadingLiquid" class="skel-block"></div>

          <template v-else-if="!uploadedLiquidFile">
            <div class="dropzone" @click="$refs.liquidFileInput.click()">
              <v-icon size="28" color="#c5cbd3" class="mb-2">mdi-cloud-upload-outline</v-icon>
              <div class="dz-title">{{ selectedLiquidFile ? selectedLiquidFile.name : 'Liquid Datei hochladen' }}</div>
              <div class="dz-sub">Klicken oder .liquid-Datei hier ablegen</div>
            </div>

            <button
              v-if="selectedLiquidFile"
              class="submit-btn mt-3"
              :disabled="uploadingLiquid"
              @click="uploadLiquidFile"
            >
              <v-progress-circular v-if="uploadingLiquid" indeterminate size="14" width="2" color="white" />
              <v-icon v-else size="16">mdi-upload-outline</v-icon>
              <span>Jetzt hochladen</span>
            </button>

            <div v-if="liquidUploadMessage" class="mt-3" :class="liquidUploadSuccess ? 'alert-success' : 'alert-error'">
              <v-icon size="16" :color="liquidUploadSuccess ? '#2E7D32' : '#D32F2F'">
                {{ liquidUploadSuccess ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}
              </v-icon>
              {{ liquidUploadMessage }}
            </div>
          </template>

          <template v-else>
            <div class="file-row">
              <div class="file-icon" style="background: rgba(46,125,50,0.1)">
                <v-icon size="18" color="#2E7D32">mdi-file-code-outline</v-icon>
              </div>
              <div class="file-body">
                <div class="file-name">{{ uploadedLiquidFile.handle }}.liquid</div>
                <div class="file-meta">{{ formatFileSize(uploadedLiquidFile.size) }}</div>
              </div>
              <div class="file-actions">
                <button
                  v-if="githubStatus.connected && githubStatus.repo"
                  class="action-btn action-sync"
                  :class="syncBadgeClass('liquid', section.id)"
                  :title="syncBadgeTitle('liquid', section.id)"
                  @click="startSync('liquid', null)"
                >
                  <v-icon size="15">mdi-sync</v-icon>
                  <span class="sync-badge" v-if="syncStatusOf('liquid', section.id)"></span>
                </button>
                <button v-if="$can('sections.edit')" class="action-btn action-edit" title="Im Editor öffnen" @click="openCodeEditor('liquid', null)">
                  <v-icon size="15">mdi-code-tags</v-icon>
                </button>
                <button class="action-btn" title="Herunterladen" @click="downloadLiquidCode">
                  <v-icon size="15">mdi-download-outline</v-icon>
                </button>
                <button class="action-btn action-danger" title="Löschen" @click="deleteLiquidCode">
                  <v-icon size="15">mdi-delete-outline</v-icon>
                </button>
              </div>
            </div>

            <button class="bs mt-3" @click="$refs.liquidFileInput.click()">
              <v-icon size="15">mdi-refresh</v-icon>
              Datei ersetzen
            </button>
          </template>
        </div>

        <!-- Snippets -->
        <div class="card-modern">
          <h2 class="section-title mb-4">
            <div class="title-icon" style="background: rgba(124,77,255,0.1)"><v-icon size="16" color="#7C4DFF">mdi-code-tags</v-icon></div>
            Snippets
            <span v-if="uploadedSnippets.length" class="title-count">{{ uploadedSnippets.length }}</span>
          </h2>

          <input
            ref="snippetFileInput"
            type="file"
            accept=".liquid"
            multiple
            style="display: none"
            @change="handleSnippetFileSelect"
          />

          <div class="dropzone" @click="$refs.snippetFileInput.click()">
            <v-icon size="28" color="#c5cbd3" class="mb-2">mdi-cloud-upload-outline</v-icon>
            <div class="dz-title">Snippet-Dateien hochladen</div>
            <div class="dz-sub">Klicken oder .liquid-Dateien hier ablegen</div>
          </div>

          <div v-if="selectedSnippets.length > 0" class="mt-3">
            <div v-for="(snippet, index) in selectedSnippets" :key="`snip-sel-${index}`" class="file-row file-row-pending">
              <div class="file-icon" style="background: rgba(124,77,255,0.1)">
                <v-icon size="18" color="#7C4DFF">mdi-file-code-outline</v-icon>
              </div>
              <div class="file-body">
                <div class="file-name">{{ snippet.name }}</div>
                <div class="file-meta">snippets/{{ snippet.name }}</div>
              </div>
              <button class="action-btn action-danger" title="Entfernen" @click="removeSelectedSnippet(index)">
                <v-icon size="15">mdi-close</v-icon>
              </button>
            </div>

            <button class="submit-btn submit-btn-purple mt-3" :disabled="uploadingSnippets" @click="uploadSnippets">
              <v-progress-circular v-if="uploadingSnippets" indeterminate size="14" width="2" color="white" />
              <v-icon v-else size="16">mdi-upload-outline</v-icon>
              <span>{{ selectedSnippets.length }} Snippet(s) hochladen</span>
            </button>
          </div>

          <div v-if="loadingSnippets" class="skel-block mt-3"></div>

          <div v-else-if="uploadedSnippets.length > 0" class="mt-4">
            <div class="divider-soft mb-3"></div>
            <div v-for="snippet in uploadedSnippets" :key="snippet.id" class="file-row">
              <div class="file-icon" style="background: rgba(124,77,255,0.1)">
                <v-icon size="18" color="#7C4DFF">mdi-file-code-outline</v-icon>
              </div>
              <div class="file-body">
                <div class="file-name">{{ snippet.fileName }}</div>
                <div class="file-meta">snippets/{{ snippet.fileName }}</div>
              </div>
              <div class="file-actions">
                <button
                  v-if="githubStatus.connected && githubStatus.repo"
                  class="action-btn action-sync"
                  :class="syncBadgeClass('snippet', snippet.id)"
                  :title="syncBadgeTitle('snippet', snippet.id)"
                  @click="startSync('snippet', snippet)"
                >
                  <v-icon size="15">mdi-sync</v-icon>
                  <span class="sync-badge" v-if="syncStatusOf('snippet', snippet.id)"></span>
                </button>
                <button v-if="$can('sections.edit')" class="action-btn action-edit" title="Im Editor öffnen" @click="openCodeEditor('snippet', snippet)">
                  <v-icon size="15">mdi-code-tags</v-icon>
                </button>
                <button class="action-btn" title="Herunterladen" @click="downloadSnippet(snippet)">
                  <v-icon size="15">mdi-download-outline</v-icon>
                </button>
                <button class="action-btn action-danger" title="Löschen" @click="deleteSnippet(snippet)">
                  <v-icon size="15">mdi-delete-outline</v-icon>
                </button>
              </div>
            </div>
          </div>

          <div v-if="snippetUploadMessage" class="mt-3" :class="snippetUploadSuccess ? 'alert-success' : 'alert-error'">
            <v-icon size="16" :color="snippetUploadSuccess ? '#2E7D32' : '#D32F2F'">
              {{ snippetUploadSuccess ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}
            </v-icon>
            {{ snippetUploadMessage }}
          </div>
        </div>

        <!-- Assets -->
        <div class="card-modern">
          <h2 class="section-title mb-4">
            <div class="title-icon" style="background: rgba(21,101,192,0.1)"><v-icon size="16" color="#1565C0">mdi-folder-multiple-outline</v-icon></div>
            Assets
            <span v-if="uploadedAssets.length" class="title-count">{{ uploadedAssets.length }}</span>
          </h2>

          <input
            ref="assetFileInput"
            type="file"
            multiple
            accept=".css,.js,.liquid,.json,.svg,.png,.jpg,.jpeg,.gif,.webp,.mp4,.webm,.woff,.woff2,.ttf,.otf,.eot"
            style="display: none"
            @change="handleAssetFileSelect"
          />

          <div class="dropzone" @click="$refs.assetFileInput.click()">
            <v-icon size="28" color="#c5cbd3" class="mb-2">mdi-cloud-upload-outline</v-icon>
            <div class="dz-title">Assets hochladen</div>
            <div class="dz-sub">CSS, JS, Liquid, Bilder, Videos, Fonts und mehr</div>
          </div>

          <div v-if="selectedAssets.length > 0" class="mt-3">
            <div v-for="(asset, index) in selectedAssets" :key="`asset-sel-${index}`" class="file-row file-row-pending">
              <div class="file-icon" :style="{ background: assetBg(asset.name) }">
                <v-icon size="18" :color="assetColor(asset.name)">{{ getAssetIcon(asset.name) }}</v-icon>
              </div>
              <div class="file-body">
                <div class="file-name">{{ asset.name }}</div>
                <div class="file-meta">{{ asset.filePath }}</div>
              </div>
              <button class="action-btn action-danger" title="Entfernen" @click="removeSelectedAsset(index)">
                <v-icon size="15">mdi-close</v-icon>
              </button>
            </div>

            <button class="submit-btn submit-btn-blue mt-3" :disabled="uploadingAssets" @click="uploadAssets">
              <v-progress-circular v-if="uploadingAssets" indeterminate size="14" width="2" color="white" />
              <v-icon v-else size="16">mdi-upload-outline</v-icon>
              <span>{{ selectedAssets.length }} Datei(en) hochladen</span>
            </button>
          </div>

          <div v-if="loadingAssets" class="skel-block mt-3"></div>

          <div v-else-if="uploadedAssets.length > 0" class="mt-4">
            <div class="divider-soft mb-3"></div>
            <div v-for="asset in uploadedAssets" :key="asset.id" class="file-row">
              <div class="file-icon" :style="{ background: assetBg(asset.fileName) }">
                <v-icon size="18" :color="assetColor(asset.fileName)">{{ getAssetIcon(asset.fileName) }}</v-icon>
              </div>
              <div class="file-body">
                <div class="file-name">{{ asset.fileName }}</div>
                <div class="file-meta">{{ asset.filePath }}</div>
              </div>
              <div class="file-actions">
                <button
                  v-if="githubStatus.connected && githubStatus.repo"
                  class="action-btn action-sync"
                  :class="syncBadgeClass('asset', asset.id)"
                  :title="syncBadgeTitle('asset', asset.id)"
                  @click="startSync('asset', asset)"
                >
                  <v-icon size="15">mdi-sync</v-icon>
                  <span class="sync-badge" v-if="syncStatusOf('asset', asset.id)"></span>
                </button>
                <button v-if="$can('sections.edit') && asset.fileCode" class="action-btn action-edit" title="Im Editor öffnen" @click="openCodeEditor('asset', asset)">
                  <v-icon size="15">mdi-code-tags</v-icon>
                </button>
                <button class="action-btn" title="Herunterladen" @click="downloadAsset(asset)">
                  <v-icon size="15">mdi-download-outline</v-icon>
                </button>
                <button class="action-btn action-danger" title="Löschen" @click="deleteAsset(asset)">
                  <v-icon size="15">mdi-delete-outline</v-icon>
                </button>
              </div>
            </div>
          </div>

          <div v-if="assetUploadMessage" class="mt-3" :class="assetUploadSuccess ? 'alert-success' : 'alert-error'">
            <v-icon size="16" :color="assetUploadSuccess ? '#2E7D32' : '#D32F2F'">
              {{ assetUploadSuccess ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}
            </v-icon>
            {{ assetUploadMessage }}
          </div>
        </div>

        <!-- Blocks -->
        <div class="card-modern">
          <h2 class="section-title mb-4">
            <div class="title-icon" style="background: rgba(255,152,0,0.1)"><v-icon size="16" color="#E65100">mdi-cube-outline</v-icon></div>
            Blocks
            <span v-if="uploadedBlocks.length" class="title-count">{{ uploadedBlocks.length }}</span>
          </h2>

          <input
            ref="blockFileInput"
            type="file"
            accept=".liquid"
            multiple
            style="display: none"
            @change="handleBlockFileSelect"
          />

          <div class="dropzone" @click="$refs.blockFileInput.click()">
            <v-icon size="28" color="#c5cbd3" class="mb-2">mdi-cloud-upload-outline</v-icon>
            <div class="dz-title">Block-Dateien hochladen</div>
            <div class="dz-sub">Klicken oder .liquid-Dateien hier ablegen</div>
          </div>

          <div v-if="selectedBlocks.length > 0" class="mt-3">
            <div v-for="(block, index) in selectedBlocks" :key="`block-sel-${index}`" class="file-row file-row-pending">
              <div class="file-icon" style="background: rgba(255,152,0,0.1)">
                <v-icon size="18" color="#E65100">mdi-cube-outline</v-icon>
              </div>
              <div class="file-body">
                <div class="file-name">{{ block.name }}</div>
                <div class="file-meta">blocks/{{ block.name }}</div>
              </div>
              <button class="action-btn action-danger" title="Entfernen" @click="removeSelectedBlock(index)">
                <v-icon size="15">mdi-close</v-icon>
              </button>
            </div>

            <button class="submit-btn submit-btn-orange mt-3" :disabled="uploadingBlocks" @click="uploadBlocks">
              <v-progress-circular v-if="uploadingBlocks" indeterminate size="14" width="2" color="white" />
              <v-icon v-else size="16">mdi-upload-outline</v-icon>
              <span>{{ selectedBlocks.length }} Block(s) hochladen</span>
            </button>
          </div>

          <div v-if="loadingBlocks" class="skel-block mt-3"></div>

          <div v-else-if="uploadedBlocks.length > 0" class="mt-4">
            <div class="divider-soft mb-3"></div>
            <div v-for="block in uploadedBlocks" :key="block.id" class="file-row">
              <div class="file-icon" style="background: rgba(255,152,0,0.1)">
                <v-icon size="18" color="#E65100">mdi-cube-outline</v-icon>
              </div>
              <div class="file-body">
                <div class="file-name">{{ block.fileName }}</div>
                <div class="file-meta">blocks/{{ block.fileName }}</div>
              </div>
              <div class="file-actions">
                <button
                  v-if="githubStatus.connected && githubStatus.repo"
                  class="action-btn action-sync"
                  :class="syncBadgeClass('block', block.id)"
                  :title="syncBadgeTitle('block', block.id)"
                  @click="startSync('block', block)"
                >
                  <v-icon size="15">mdi-sync</v-icon>
                  <span class="sync-badge" v-if="syncStatusOf('block', block.id)"></span>
                </button>
                <button v-if="$can('sections.edit')" class="action-btn action-edit" title="Im Editor öffnen" @click="openCodeEditor('block', block)">
                  <v-icon size="15">mdi-code-tags</v-icon>
                </button>
                <button class="action-btn" title="Herunterladen" @click="downloadBlock(block)">
                  <v-icon size="15">mdi-download-outline</v-icon>
                </button>
                <button class="action-btn action-danger" title="Löschen" @click="deleteBlock(block)">
                  <v-icon size="15">mdi-delete-outline</v-icon>
                </button>
              </div>
            </div>
          </div>

          <div v-if="blockUploadMessage" class="mt-3" :class="blockUploadSuccess ? 'alert-success' : 'alert-error'">
            <v-icon size="16" :color="blockUploadSuccess ? '#2E7D32' : '#D32F2F'">
              {{ blockUploadSuccess ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}
            </v-icon>
            {{ blockUploadMessage }}
          </div>
        </div>
        </div>

        <!-- Anleitungen (volle Breite der linken Spalte, unter files-grid) -->
        <div class="card-modern">
          <div class="guide-card-head">
            <h2 class="section-title" style="margin: 0">
              <div class="title-icon"><v-icon size="16" color="#0a1628">mdi-book-open-page-variant-outline</v-icon></div>
              Anleitungen
              <span v-if="linkedGuides.length" class="guide-count">{{ linkedGuides.length }}</span>
            </h2>
            <div v-if="$can('sections.edit') && linkedGuides.length" class="d-flex" style="gap: 8px">
              <button class="bs" @click="openNewGuideEditor">
                <v-icon size="15">mdi-plus</v-icon><span>Anlegen</span>
              </button>
              <button class="bs" @click="openPickGuideDialog">
                <v-icon size="15">mdi-bookmark-outline</v-icon><span>Verknüpfen</span>
              </button>
            </div>
          </div>

          <div v-if="loadingLinkedGuides" class="guide-loading">
            <v-progress-circular indeterminate size="22" width="2" color="#888" />
          </div>

          <div v-else-if="linkedGuides.length" class="guide-list">
            <div v-for="g in linkedGuides" :key="g.id" class="guide-row" @click="openGuideViewer(g)">
              <div class="guide-row-icon">
                <v-icon size="16" color="#5B5BD6">mdi-book-open-page-variant-outline</v-icon>
              </div>
              <div class="guide-row-main">
                <div class="guide-row-title">{{ g.title }}</div>
                <div v-if="contentPreviewText(g)" class="guide-row-preview">{{ contentPreviewText(g) }}</div>
              </div>
              <div class="guide-row-actions" @click.stop>
                <button class="guide-act" title="Ansehen" @click="openGuideViewer(g)">
                  <v-icon size="16">mdi-eye-outline</v-icon>
                </button>
                <button v-if="$can('sections.edit')" class="guide-act" title="Bearbeiten" @click="openEditGuideEditor(g)">
                  <v-icon size="16">mdi-pencil-outline</v-icon>
                </button>
                <button v-if="$can('sections.edit')" class="guide-act guide-act-danger" title="Trennen" @click="openUnlinkGuideDialog(g)">
                  <v-icon size="16">mdi-link-variant-off</v-icon>
                </button>
              </div>
            </div>
          </div>

          <div v-else class="empty-guide">
            <v-icon size="36" color="#c5cbd3" class="mb-2">mdi-book-open-page-variant-outline</v-icon>
            <div class="empty-guide-title">Noch keine Anleitung</div>
            <div class="empty-guide-desc">Verknüpfe Dokumentation mit dieser Section — du kannst mehrere Anleitungen hinzufügen.</div>
            <button v-if="$can('sections.edit')" class="submit-btn mt-3" @click="openNewGuideEditor">
              <v-icon size="15">mdi-plus</v-icon>
              <span>Anleitung anlegen</span>
            </button>
            <button v-if="$can('sections.edit')" class="text-link-btn mt-2" @click="openPickGuideDialog">
              <v-icon size="13">mdi-bookmark-outline</v-icon>
              <span>Vorhandene Anleitung verknüpfen</span>
            </button>
          </div>

          <div v-if="guideMessage" class="mt-3" :class="guideSuccess ? 'alert-success' : 'alert-error'">
            <v-icon size="16" :color="guideSuccess ? '#2E7D32' : '#D32F2F'">
              {{ guideSuccess ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}
            </v-icon>
            {{ guideMessage }}
          </div>
        </div>
      </div>

      <!-- ===================== RECHTE SPALTE ===================== -->
      <div class="col-right">
        <!-- Status -->
        <div class="card-modern">
          <h2 class="section-title mb-4">
            <div class="title-icon"><v-icon size="16" color="#0a1628">mdi-progress-check</v-icon></div>
            Status
          </h2>

          <div
            class="status-option"
            :class="{ 'status-active': section.status === 'draft' }"
            @click="setStatus('draft')"
          >
            <div class="status-dot" :class="{ 'status-dot-on': section.status === 'draft' }">
              <v-icon v-if="section.status === 'draft'" size="14" color="white">mdi-check</v-icon>
            </div>
            <div class="status-icon" style="background: rgba(91,91,214,0.1)">
              <v-icon size="16" color="#5B5BD6">mdi-file-document-edit-outline</v-icon>
            </div>
            <div class="status-body">
              <div class="status-label">Entwurf</div>
              <div class="status-desc">Noch nicht umgesetzt oder fertig</div>
            </div>
          </div>

          <div
            class="status-option mt-2"
            :class="{ 'status-active': section.status === 'completed' }"
            @click="setStatus('completed')"
          >
            <div class="status-dot" :class="{ 'status-dot-on': section.status === 'completed' }">
              <v-icon v-if="section.status === 'completed'" size="14" color="white">mdi-check</v-icon>
            </div>
            <div class="status-icon" style="background: rgba(46,125,50,0.1)">
              <v-icon size="16" color="#2E7D32">mdi-check-circle-outline</v-icon>
            </div>
            <div class="status-body">
              <div class="status-label">Fertiggestellt</div>
              <div class="status-desc">Umgesetzt und einsatzbereit</div>
            </div>
          </div>
        </div>

        <!-- Vertriebskanäle -->
        <div class="card-modern">
          <h2 class="section-title mb-4">
            <div class="title-icon"><v-icon size="16" color="#0a1628">mdi-store-outline</v-icon></div>
            Vertriebskanäle
          </h2>

          <div v-if="section.status !== 'completed'" class="info-banner mb-3">
            <v-icon size="14" color="#5B5BD6">mdi-information-outline</v-icon>
            <span>
              Sections müssen erst auf <strong>Fertiggestellt</strong> stehen, um in einen Vertriebskanal aufgenommen zu werden.
            </span>
          </div>

          <div
            v-for="ch in availableChannels"
            :key="ch.id"
            class="status-option"
            :class="{
              'status-active': hasChannel(ch.id) && section.status === 'completed',
              'status-disabled': section.status !== 'completed',
            }"
            @click="section.status === 'completed' && toggleChannel(ch.id)"
            style="margin-bottom: 8px"
          >
            <div class="status-dot status-dot-square" :class="{ 'status-dot-on': hasChannel(ch.id) && section.status === 'completed' }">
              <v-icon v-if="hasChannel(ch.id) && section.status === 'completed'" size="14" color="white">mdi-check</v-icon>
            </div>
            <div class="status-icon" style="background: rgba(46,125,50,0.1)">
              <v-icon size="16" color="#2E7D32">{{ ch.icon }}</v-icon>
            </div>
            <div class="status-body">
              <div class="status-label">{{ ch.label }}</div>
              <div class="status-desc">In der Shopify-App für Merchants sichtbar</div>
            </div>
            <v-icon v-if="section.status !== 'completed'" size="16" color="#c5cbd3">mdi-lock-outline</v-icon>
          </div>
          <div v-if="channelMessage" class="alert-error mt-2" style="font-size: 12px">
            <v-icon size="14" color="#D32F2F">mdi-alert-circle-outline</v-icon>
            {{ channelMessage }}
          </div>
        </div>

        <!-- Tag (Verwendungszweck) -->
        <div class="card-modern">
          <h2 class="section-title mb-4">
            <div class="title-icon"><v-icon size="16" color="#0a1628">mdi-tag-outline</v-icon></div>
            Tag
          </h2>

          <div class="combo-wrap" ref="purposeDropdown">
          <button
            type="button"
            class="iw iw-trigger"
            :class="{ 'iw-f': purposeMenuOpen }"
            @click="purposeMenuOpen = !purposeMenuOpen"
          >
            <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-label-outline</v-icon>
            <span class="ii-val">{{ currentPurposeLabel }}</span>
            <v-icon size="14" color="#888" :class="{ 'chev-open': purposeMenuOpen }">mdi-chevron-down</v-icon>
          </button>
          <div v-if="purposeMenuOpen" class="combo-menu">
            <button type="button" class="combo-option" @click="selectPurpose(null)">
              <span class="combo-label">Kein Tag</span>
              <v-icon v-if="!section.purpose" size="14" color="#2E7D32" style="margin-left: auto">mdi-check</v-icon>
            </button>
            <div class="combo-divider"></div>
            <button
              v-for="p in purposeOptions"
              :key="p.value"
              type="button"
              class="combo-option"
              @click="selectPurpose(p.value)"
            >
              <span class="combo-label">{{ p.label }}</span>
              <span class="combo-hint">{{ p.desc }}</span>
              <v-icon v-if="section.purpose === p.value" size="14" color="#2E7D32" style="margin-left: 8px">mdi-check</v-icon>
            </button>
          </div>
          </div>
        </div>

        <!-- Vorschaubild -->
        <div class="card-modern">
          <h2 class="section-title mb-4">
            <div class="title-icon"><v-icon size="16" color="#0a1628">mdi-image-outline</v-icon></div>
            Vorschaubild
          </h2>

          <input
            ref="imageFileInput"
            type="file"
            accept="image/*"
            style="display: none"
            @change="handleImageFileSelect"
          />

          <div class="image-preview" @click="$refs.imageFileInput.click()">
            <v-img
              :key="imageKey"
              :src="`/api/sections/${section.id}/image?t=${imageKey}`"
              cover
              aspect-ratio="1"
            >
              <template #placeholder>
                <div class="image-fallback">
                  <v-progress-circular indeterminate size="28" width="2" color="#c5cbd3" />
                </div>
              </template>
              <template #error>
                <div class="image-fallback">
                  <v-icon size="40" color="#c5cbd3">mdi-image-off-outline</v-icon>
                  <div class="fallback-text">Kein Bild</div>
                </div>
              </template>
            </v-img>
          </div>

          <button class="bs mt-3" :disabled="uploadingImage" @click="$refs.imageFileInput.click()">
            <v-progress-circular v-if="uploadingImage" indeterminate size="14" width="2" color="#0a1628" />
            <v-icon v-else size="15">mdi-image-edit-outline</v-icon>
            Bild ändern
          </button>

          <div v-if="imageUploadMessage" class="mt-3" :class="imageUploadSuccess ? 'alert-success' : 'alert-error'">
            <v-icon size="16" :color="imageUploadSuccess ? '#2E7D32' : '#D32F2F'">
              {{ imageUploadSuccess ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}
            </v-icon>
            {{ imageUploadMessage }}
          </div>
        </div>

      </div>
    </div>


    <!-- Bulk-Sync Bestätigungs-Dialog -->
    <div v-if="bulkSyncDialog" class="dialog-backdrop" @click.self="bulkSyncDialog = false"></div>
    <v-dialog v-model="bulkSyncDialog" max-width="520" :scrim="false" persistent>
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" @click="bulkSyncDialog = false"><v-icon size="18">mdi-close</v-icon></button>

        <div class="dh">
          <div class="dh-icon"><v-icon size="22" color="#0a1628">mdi-sync</v-icon></div>
          <div>
            <div class="dh-title">Synchronisieren?</div>
            <div class="dh-sub">{{ githubStatus.repo?.fullName }} · {{ githubStatus.repo?.branch }}</div>
          </div>
        </div>

        <div class="db">
          <p class="bulk-lead">
            <strong>{{ bulkFilesToSync.length }}</strong> {{ bulkFilesToSync.length === 1 ? 'Datei wird' : 'Dateien werden' }}
            von SAM ins Repository gepusht.
            <span v-if="bulkDifferentCount > 0" class="bulk-warn">
              {{ bulkDifferentCount }} {{ bulkDifferentCount === 1 ? 'Datei wird' : 'Dateien werden' }} im Repo überschrieben.
            </span>
          </p>

          <div v-if="bulkFilesToSync.length > 0" class="bulk-list">
            <div
              v-for="f in bulkFilesToSync"
              :key="`${f.type}:${f.id}`"
              class="bulk-row"
            >
              <div class="bulk-row-dot" :style="{ background: bulkActionColor(syncStatusOf(f.type, f.id)) }"></div>
              <div class="bulk-row-body">
                <div class="bulk-row-name">{{ fileLabel(f) }}</div>
                <div class="bulk-row-meta">
                  <span :style="{ color: fileTypeColor(f.type) }">{{ fileTypeLabel(f.type) }}</span>
                  <span class="bulk-row-sep">·</span>
                  <span>{{ bulkActionLabel(syncStatusOf(f.type, f.id)) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="bulkAlreadySyncedCount > 0" class="bulk-muted">
            {{ bulkAlreadySyncedCount }} {{ bulkAlreadySyncedCount === 1 ? 'Datei ist' : 'Dateien sind' }} bereits synchron und werden übersprungen.
          </div>
        </div>

        <div class="dff">
          <button class="bo" @click="bulkSyncDialog = false">Abbrechen</button>
          <button class="bp" :disabled="bulkFilesToSync.length === 0" @click="bulkSync">
            <v-icon size="15">mdi-sync</v-icon>
            <span>Synchronisieren</span>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Toast / Snackbar (unten rechts) -->
    <v-snackbar
      v-model="toast.show"
      :color="toast.type === 'success' ? '#2E7D32' : toast.type === 'warning' ? '#E65100' : '#D32F2F'"
      :timeout="toast.timeout || 3500"
      location="bottom right"
    >
      <div class="d-flex align-center" style="gap: 10px">
        <v-icon size="18" color="white">
          {{ toast.type === 'success' ? 'mdi-check-circle' : toast.type === 'warning' ? 'mdi-alert' : 'mdi-alert-circle' }}
        </v-icon>
        <div>
          <div style="font-weight: 700; font-size: 13px">{{ toast.title }}</div>
          <div v-if="toast.message" style="font-size: 12px; opacity: 0.9">{{ toast.message }}</div>
        </div>
      </div>
    </v-snackbar>

    <!-- Guide Editor Modal -->
    <GuideEditor
      v-model="guideEditorOpen"
      :initial-content="guideEditorContent"
      :initial-title="guideEditorTitle"
      :is-new="guideEditorMode === 'new'"
      :saving="savingGuide"
      @save="saveGuide"
    />

    <!-- Code Editor (Monaco) – für Liquid / Snippets / Blocks / Assets
         v-if statt v-model: bei Schließen wird die Komponente vollständig
         unmountet → Monaco + alle Workers/Listener werden zuverlässig freigegeben. -->
    <CodeEditor
      v-if="codeEditor.open"
      :content="codeEditor.content"
      :language="codeEditor.language"
      :title="codeEditor.title"
      :file-path="codeEditor.filePath"
      :icon="codeEditor.icon"
      :icon-color="codeEditor.iconColor"
      :icon-bg="codeEditor.iconBg"
      :saving="codeEditor.saving"
      @save="onCodeEditorSave"
      @close="codeEditor.open = false"
    />

    <!-- Sync Dialog -->
    <div v-if="syncDialog" class="sync-overlay" @click.self="!syncing && closeSyncDialog()">
      <div class="sync-shell">
        <div class="sync-header">
          <div class="d-flex align-center" style="gap: 12px; min-width: 0; flex: 1">
            <div class="sync-header-icon" :class="`sync-icon-${syncState.status}`">
              <v-icon size="20" :color="syncState.status === 'different' ? '#E65100' : syncState.status === 'not_in_repo' ? '#5B5BD6' : '#2E7D32'">
                {{ syncState.status === 'different' ? 'mdi-source-pull' : syncState.status === 'not_in_repo' ? 'mdi-plus-box-outline' : 'mdi-sync' }}
              </v-icon>
            </div>
            <div style="min-width: 0">
              <div class="sync-title">{{ syncHeaderTitle }}</div>
              <div class="sync-sub">
                <span class="sync-path">{{ syncState.path }}</span>
                <span class="sync-repo">· {{ githubStatus.repo?.fullName }}@{{ githubStatus.repo?.branch }}</span>
              </div>
            </div>
          </div>
          <button class="sync-close" :disabled="syncing" @click="closeSyncDialog">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <div class="sync-body">
          <!-- Identical -->
          <div v-if="syncState.status === 'identical'" class="sync-state">
            <v-icon size="48" color="#2E7D32" class="mb-3">mdi-check-circle-outline</v-icon>
            <div class="sync-state-title">Datei ist bereits synchron</div>
            <div class="sync-state-desc">Lokale Version und Repository stimmen überein — nichts zu tun.</div>
          </div>

          <!-- Not in Repo -->
          <div v-else-if="syncState.status === 'not_in_repo'" class="sync-state">
            <v-icon size="48" color="#5B5BD6" class="mb-3">mdi-file-plus-outline</v-icon>
            <div class="sync-state-title">Datei ist noch nicht im Repository</div>
            <div class="sync-state-desc">Soll die Datei jetzt unter <code class="code-inline">{{ syncState.path }}</code> angelegt werden?</div>
          </div>

          <!-- Different -->
          <div v-else-if="syncState.status === 'different'" class="sync-diff">
            <div class="sync-diff-intro">
              Die Datei unterscheidet sich. Wähle eine Richtung — die andere Version wird überschrieben.
            </div>
            <div class="diff-grid">
              <div class="diff-col">
                <div class="diff-head diff-head-sam">
                  <v-icon size="14" color="#0a1628">mdi-database-outline</v-icon>
                  <span>SAM (lokal)</span>
                  <span class="diff-size">{{ formatBytes(syncState.localContent?.length) }}</span>
                </div>
                <pre class="diff-pre">{{ syncState.localContent || '(leer)' }}</pre>
              </div>
              <div class="diff-col">
                <div class="diff-head diff-head-git">
                  <v-icon size="14" color="#0a1628">mdi-github</v-icon>
                  <span>Repository</span>
                  <span class="diff-size">{{ formatBytes(syncState.remoteContent?.length) }}</span>
                </div>
                <pre class="diff-pre">{{ syncState.remoteContent || '(leer)' }}</pre>
              </div>
            </div>
          </div>
        </div>

        <div class="sync-footer">
          <button class="bo" :disabled="syncing" @click="closeSyncDialog">Abbrechen</button>

          <template v-if="syncState.status === 'not_in_repo'">
            <button class="bp bp-indigo" :disabled="syncing" @click="executeSync('push')">
              <v-progress-circular v-if="syncing" indeterminate size="14" width="2" color="white" />
              <v-icon v-else size="15">mdi-plus</v-icon>
              <span>In Repo anlegen</span>
            </button>
          </template>

          <template v-else-if="syncState.status === 'different'">
            <button class="bp bp-outline" :disabled="syncing" @click="executeSync('pull')">
              <v-progress-circular v-if="syncing && syncDirection === 'pull'" indeterminate size="14" width="2" color="#0a1628" />
              <v-icon v-else size="15">mdi-arrow-left-bold-outline</v-icon>
              <span>Git → SAM</span>
            </button>
            <button class="bp" :disabled="syncing" @click="executeSync('push')">
              <v-progress-circular v-if="syncing && syncDirection === 'push'" indeterminate size="14" width="2" color="white" />
              <v-icon v-else size="15">mdi-arrow-right-bold-outline</v-icon>
              <span>SAM → Git</span>
            </button>
          </template>

          <template v-else-if="syncState.status === 'identical'">
            <button class="bp" @click="closeSyncDialog">Schließen</button>
          </template>
        </div>
      </div>
    </div>

    <!-- Guide Viewer Modal -->
    <div v-if="guideViewer.open" class="viewer-overlay" @click.self="guideViewer.open = false">
      <div class="viewer-shell">
        <div class="viewer-header">
          <div class="d-flex align-center" style="gap: 12px">
            <div class="header-icon"><v-icon size="18" color="#0a1628">mdi-book-open-page-variant-outline</v-icon></div>
            <div>
              <div class="header-title">{{ guideViewer.title || 'Anleitung' }}</div>
              <div class="header-sub">{{ section.title }}</div>
            </div>
          </div>
          <div class="d-flex align-center" style="gap: 8px">
            <button class="viewer-close" @click="guideViewer.open = false" title="Schließen">
              <v-icon size="18">mdi-close</v-icon>
            </button>
          </div>
        </div>
        <div class="viewer-body">
          <div v-if="guideViewer.loading" class="guide-loading">
            <v-progress-circular indeterminate size="24" width="2" color="#888" />
          </div>
          <div v-else class="viewer-content" v-html="guideViewer.content"></div>
        </div>
      </div>
    </div>

    <!-- Floating Save (nur wenn Edit-Recht) -->
    <transition name="slide-fade">
      <div v-if="hasChanges && $can('sections.edit')" class="save-floating">
        <button class="save-btn" :disabled="saving" @click="saveChanges">
          <v-progress-circular v-if="saving" indeterminate size="14" width="2" color="white" />
          <v-icon v-else size="16">mdi-content-save-outline</v-icon>
          <span>Änderungen speichern</span>
        </button>
      </div>
    </transition>

    <!-- Delete Confirm Dialog -->
    <div v-if="deleteDialog" class="dialog-backdrop" @click.self="!deleting && (deleteDialog = false)"></div>
    <v-dialog v-model="deleteDialog" max-width="460" :scrim="false" persistent>
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" @click="deleteDialog = false"><v-icon size="18">mdi-close</v-icon></button>
        <div class="dh">
          <div class="dh-icon" style="background: rgba(211,47,47,0.1)">
            <v-icon size="22" color="#D32F2F">mdi-delete-outline</v-icon>
          </div>
          <div>
            <div class="dh-title">Section löschen?</div>
            <div class="dh-sub">Diese Aktion kann nicht rückgängig gemacht werden.</div>
          </div>
        </div>
        <div class="db">
          <p class="text-body-2" style="color: #0a1628; margin: 0 0 10px 0">
            <strong>{{ section.title }}</strong> ({{ section.handle }}) wird endgültig gelöscht – inklusive aller Snippets, Blocks und Assets.
          </p>
          <p class="text-caption" style="color: #888; margin: 0">
            Die Datei im verknüpften GitHub-Repository wird <strong>nicht</strong> automatisch entfernt.
            Verknüpfte Tasks bleiben erhalten, verlieren aber die Section-Referenz.
          </p>
          <div v-if="deleteError" class="alert-error mt-3">
            <v-icon size="14" color="#D32F2F">mdi-alert-circle-outline</v-icon>
            {{ deleteError }}
          </div>
        </div>
        <div class="dff">
          <button class="bo" @click="deleteDialog = false" :disabled="deleting">Abbrechen</button>
          <button class="bp btn-danger" @click="executeDelete" :disabled="deleting">
            <v-progress-circular v-if="deleting" indeterminate size="14" width="2" color="white" />
            <v-icon v-else size="15">mdi-delete-outline</v-icon>
            Endgültig löschen
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Pick Guide from Library Dialog -->
    <div v-if="pickGuideDialog" class="dialog-backdrop" @click.self="!pickingGuide && (pickGuideDialog = false)"></div>
    <v-dialog v-model="pickGuideDialog" max-width="640" :scrim="false" persistent>
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" @click="pickGuideDialog = false"><v-icon size="18">mdi-close</v-icon></button>
        <div class="dh">
          <div class="dh-icon"><v-icon size="22" color="#0a1628">mdi-book-open-page-variant-outline</v-icon></div>
          <div>
            <div class="dh-title">Anleitungen verknüpfen</div>
            <div class="dh-sub">Wähle eine oder mehrere Anleitungen aus der Bibliothek.</div>
          </div>
        </div>
        <div class="db pick-guide-body">
          <div class="pick-search">
            <v-icon size="16" color="#888">mdi-magnify</v-icon>
            <input
              v-model="pickGuideSearch"
              type="text"
              placeholder="Anleitung suchen…"
              class="pick-search-input"
              autofocus
            />
            <v-icon
              v-if="pickGuideSearch"
              size="14"
              color="#888"
              class="pick-search-clear"
              @click="pickGuideSearch = ''"
            >mdi-close</v-icon>
          </div>

          <div v-if="loadingGuideLibrary" class="pick-empty">
            <v-progress-circular indeterminate size="22" width="2" color="#888" />
            <div class="text-caption mt-2" style="color: #888">Lade Anleitungen…</div>
          </div>

          <div v-else-if="filteredGuideLibrary.length === 0" class="pick-empty">
            <v-icon size="32" color="#c5cbd3" class="mb-2">mdi-book-open-page-variant-outline</v-icon>
            <div class="text-caption" style="color: #888">
              {{ guideLibrary.length === 0 ? 'Noch keine Anleitungen in der Bibliothek.' : 'Keine Treffer.' }}
            </div>
          </div>

          <div v-else class="pick-list">
            <button
              v-for="g in filteredGuideLibrary"
              :key="g.id"
              class="pick-item"
              :class="{ 'pick-item-active': pickedGuideIds.includes(g.id), 'pick-item-linked': isGuideLinked(g.id) }"
              :disabled="isGuideLinked(g.id)"
              @click="togglePickGuide(g.id)"
            >
              <div class="pick-guide-icon">
                <v-icon size="16" color="#5B5BD6">mdi-book-open-page-variant-outline</v-icon>
              </div>
              <div class="pick-text">
                <div class="pick-title">{{ g.title }}</div>
                <div v-if="contentPreviewText(g)" class="pick-preview">{{ contentPreviewText(g) }}</div>
                <div v-if="(g.sections || []).length > 0" class="pick-meta">
                  Verknüpft mit {{ (g.sections || []).length }} Section{{ (g.sections || []).length === 1 ? '' : 's' }}
                </div>
              </div>
              <span v-if="isGuideLinked(g.id)" class="pick-linked-badge">Verknüpft</span>
              <v-icon v-else-if="pickedGuideIds.includes(g.id)" size="18" color="#2E7D32">mdi-check-circle</v-icon>
              <v-icon v-else size="18" color="#c5cbd3">mdi-checkbox-blank-circle-outline</v-icon>
            </button>
          </div>

          <div v-if="pickGuideError" class="alert-error mt-3">
            <v-icon size="14" color="#D32F2F">mdi-alert-circle-outline</v-icon>
            {{ pickGuideError }}
          </div>
        </div>
        <div class="dff">
          <button class="bo" @click="pickGuideDialog = false" :disabled="pickingGuide">Abbrechen</button>
          <button
            class="bp"
            :disabled="!pickedGuideIds.length || pickingGuide"
            @click="applyPickedGuides"
          >
            <v-progress-circular v-if="pickingGuide" indeterminate size="14" width="2" color="white" />
            <v-icon v-else size="15">mdi-link-variant</v-icon>
            {{ pickedGuideIds.length > 1 ? `${pickedGuideIds.length} Anleitungen verknüpfen` : 'Anleitung verknüpfen' }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Unlink Guide Confirm Dialog -->
    <div v-if="unlinkGuideDialog" class="dialog-backdrop" @click.self="!unlinkingGuide && (unlinkGuideDialog = false)"></div>
    <v-dialog v-model="unlinkGuideDialog" max-width="460" :scrim="false" persistent>
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" @click="unlinkGuideDialog = false"><v-icon size="18">mdi-close</v-icon></button>
        <div class="dh">
          <div class="dh-icon" style="background: rgba(245,158,11,0.1)">
            <v-icon size="22" color="#E65100">mdi-link-variant-off</v-icon>
          </div>
          <div>
            <div class="dh-title">Anleitung trennen?</div>
            <div class="dh-sub">Die Anleitung wird nur von dieser Section entkoppelt.</div>
          </div>
        </div>
        <div class="db">
          <p class="text-body-2" style="color: #0a1628; margin: 0 0 8px 0">
            <strong>{{ unlinkGuideTarget?.title }}</strong> bleibt in der Bibliothek <strong>erhalten</strong>
            und ist weiterhin in <a href="/anleitungen" class="text-link-inline">Anleitungen</a> verfügbar.
            Nur die Verknüpfung zu dieser Section wird gelöst.
          </p>
          <p class="text-caption" style="color: #888; margin: 0">
            Du kannst die Anleitung jederzeit wieder verknüpfen über „Verknüpfen".
          </p>
          <div v-if="unlinkGuideError" class="alert-error mt-3">
            <v-icon size="14" color="#D32F2F">mdi-alert-circle-outline</v-icon>
            {{ unlinkGuideError }}
          </div>
        </div>
        <div class="dff">
          <button class="bo" @click="unlinkGuideDialog = false" :disabled="unlinkingGuide">Abbrechen</button>
          <button class="bp" :disabled="unlinkingGuide" @click="confirmUnlinkGuide">
            <v-progress-circular v-if="unlinkingGuide" indeterminate size="14" width="2" color="white" />
            <v-icon v-else size="15">mdi-link-variant-off</v-icon>
            Trennen
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import GuideEditor from '@/components/GuideEditor.vue';
import CodeEditor from '@/components/CodeEditor.vue';

export default {
  name: 'SectionDetails',
  components: { GuideEditor, CodeEditor },
  data() {
    return {
      loading: true,
      section: {},
      originalSection: {},
      categories: [],
      loadingCategories: false,
      categoriesLoaded: false,
      categoryMenuOpen: false,
      focusField: null,

      // Anleitungen (n:m — mehrere Library-Guides pro Section)
      linkedGuides: [],
      loadingLinkedGuides: false,
      guideMessage: '',
      guideSuccess: false,

      // Guide-Editor (Library-Guide anlegen / bearbeiten)
      guideEditorOpen: false,
      savingGuide: false,
      guideEditorMode: 'new',     // 'new' | 'edit'
      guideEditorId: null,
      guideEditorTitle: '',
      guideEditorContent: '',

      // Guide-Viewer
      guideViewer: { open: false, loading: false, title: '', content: '' },

      // GitHub Sync
      githubStatus: { connected: false, repo: null },
      syncDialog: false,
      syncing: false,
      syncDirection: null,
      syncState: { status: null, path: '', localContent: '', remoteContent: '', sha: null, type: null, target: null },
      // Map 'liquid:<id>' / 'snippet:<id>' / 'block:<id>' / 'asset:<id>' → 'identical' | 'different' | 'not_in_repo'
      syncStatuses: {},
      bulkSyncing: false,
      bulkSyncProgress: { current: 0, total: 0 },
      bulkSyncDialog: false,
      toast: { show: false, type: 'success', title: '', message: '', timeout: 3500 },

      // Delete
      deleteDialog: false,
      moreMenuOpen: false,
      deleting: false,
      deleteError: '',

      // Pick guides from library (Mehrfachauswahl)
      pickGuideDialog: false,
      pickGuideSearch: '',
      guideLibrary: [],
      loadingGuideLibrary: false,
      pickedGuideIds: [],
      pickingGuide: false,
      pickGuideError: '',

      // Unlink (Section ↔ Guide trennen)
      unlinkGuideDialog: false,
      unlinkGuideTarget: null,
      unlinkingGuide: false,
      unlinkGuideError: '',

      // Section-Navigation (vor/zurück)
      allSectionIds: [],

      selectedLiquidFile: null,
      uploadingLiquid: false,
      liquidUploadMessage: '',
      liquidUploadSuccess: false,
      uploadedLiquidFile: null,
      loadingLiquid: false,

      selectedSnippets: [],
      uploadingSnippets: false,
      snippetUploadMessage: '',
      snippetUploadSuccess: false,
      uploadedSnippets: [],
      loadingSnippets: false,

      selectedBlocks: [],
      uploadingBlocks: false,
      blockUploadMessage: '',
      blockUploadSuccess: false,
      uploadedBlocks: [],
      loadingBlocks: false,

      // Code-Editor (Monaco)
      codeEditor: {
        open: false,
        type: null,    // 'liquid' | 'snippet' | 'block' | 'asset'
        target: null,  // bei snippet/block/asset: das Datei-Objekt
        content: '',
        language: 'html',
        title: '',
        filePath: '',
        icon: 'mdi-code-tags',
        iconColor: '#2E7D32',
        iconBg: 'rgba(46,125,50,0.1)',
        saving: false,
      },

      selectedImageFile: null,
      uploadingImage: false,
      imageUploadMessage: '',
      imageUploadSuccess: false,
      imageKey: Date.now(),

      selectedAssets: [],
      uploadingAssets: false,
      uploadedAssets: [],
      assetUploadMessage: '',
      assetUploadSuccess: false,
      loadingAssets: false,

      saving: false,
      saveMessage: '',
      saveSuccess: false,

      // Sales Channels
      availableChannels: [],
      channelMessage: '',

      // Purpose-Tag Dropdown
      purposeMenuOpen: false,
      purposeOptions: [
        { value: 'intern', label: 'Intern', desc: 'Nur für Tests' },
        { value: 'intern_app', label: 'Intern & App', desc: 'Tests + Verkauf in App' },
        { value: 'app', label: 'App', desc: 'Nur in Shopify App' },
      ],
    };
  },

  computed: {
    isPremium() { return this.section.price > 0; },
    filteredGuideLibrary() {
      const q = (this.pickGuideSearch || '').trim().toLowerCase();
      if (!q) return this.guideLibrary;
      return this.guideLibrary.filter((g) =>
        (g.title || '').toLowerCase().includes(q)
      );
    },
    currentCategoryName() {
      const cat = this.categories.find((c) => c.id === this.section.categoryId);
      return cat?.name || 'Kategorie auswählen';
    },
    currentPurposeLabel() {
      if (!this.section.purpose) return 'Kein Tag';
      const p = this.purposeOptions.find((o) => o.value === this.section.purpose);
      return p ? p.label : 'Kein Tag';
    },
    hasChanges() {
      return (
        this.section.categoryId !== this.originalSection.categoryId ||
        this.section.title !== this.originalSection.title ||
        this.section.demoStore !== this.originalSection.demoStore ||
        this.section.price !== this.originalSection.price ||
        this.section.status !== this.originalSection.status ||
        this.section.purpose !== this.originalSection.purpose
      );
    },
    syncHeaderTitle() {
      if (!this.syncState.status) return 'Synchronisieren';
      if (this.syncState.status === 'identical') return 'Synchron';
      if (this.syncState.status === 'not_in_repo') return 'Datei anlegen';
      return 'Unterschiede gefunden';
    },
    currentIndex() {
      const id = Number(this.$route.params.id);
      return this.allSectionIds.findIndex((sid) => sid === id);
    },
    prevSectionId() {
      if (this.currentIndex < 0) return null;
      return this.allSectionIds[this.currentIndex - 1] || null;
    },
    nextSectionId() {
      if (this.currentIndex < 0) return null;
      return this.allSectionIds[this.currentIndex + 1] || null;
    },

    // Alle synchronisierbaren Dateien als normalisierte Liste
    allSyncableFiles() {
      const list = [];
      if (this.uploadedLiquidFile) {
        list.push({ type: 'liquid', id: this.section.id, target: null });
      }
      for (const s of this.uploadedSnippets) list.push({ type: 'snippet', id: s.id, target: s });
      for (const b of this.uploadedBlocks) list.push({ type: 'block', id: b.id, target: b });
      for (const a of this.uploadedAssets) {
        if (a.fileCode || !a.fileData) list.push({ type: 'asset', id: a.id, target: a });
      }
      return list;
    },
    totalSyncableFiles() { return this.allSyncableFiles.length; },
    syncedCount() {
      return this.allSyncableFiles.filter((f) => this.syncStatusOf(f.type, f.id) === 'identical').length;
    },
    overallSyncStatus() {
      const total = this.totalSyncableFiles;
      if (total === 0) return 'none';
      const synced = this.syncedCount;
      if (synced === total) return 'all-synced';
      if (synced === 0) return 'none-synced';
      return 'partial';
    },
    overallIcon() {
      if (this.overallSyncStatus === 'all-synced') return 'mdi-check-circle';
      if (this.overallSyncStatus === 'partial') return 'mdi-sync-alert';
      return 'mdi-alert-circle-outline';
    },
    overallLabel() {
      if (this.overallSyncStatus === 'all-synced') return 'Synchronisiert';
      if (this.overallSyncStatus === 'partial') return 'Teilweise synchronisiert';
      return 'Nicht synchronisiert';
    },
    overallSyncTitle() {
      if (this.overallSyncStatus === 'all-synced') return 'Alle Dateien sind mit dem Repo synchron';
      if (this.overallSyncStatus === 'partial') return `${this.syncedCount} von ${this.totalSyncableFiles} Dateien sind synchron`;
      return 'Noch keine Datei ist synchron';
    },

    bulkFilesToSync() {
      return this.allSyncableFiles.filter((f) => this.syncStatusOf(f.type, f.id) !== 'identical');
    },
    bulkDifferentCount() {
      return this.bulkFilesToSync.filter((f) => this.syncStatusOf(f.type, f.id) === 'different').length;
    },
    bulkAlreadySyncedCount() {
      return this.allSyncableFiles.length - this.bulkFilesToSync.length;
    },
  },

  watch: {
    section: {
      handler() { if (this.saveMessage) this.saveMessage = ''; },
      deep: true,
    },
    '$route.params.id'(newId, oldId) {
      if (newId && newId !== oldId) this.reloadAll();
    },
  },

  async mounted() {
    await this.reloadAll(true);
    document.addEventListener('click', this.handleOutsideClick);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },

  methods: {
    openDeleteDialog() {
      this.deleteError = '';
      this.deleteDialog = true;
    },
    async executeDelete() {
      this.deleting = true;
      this.deleteError = '';
      try {
        await axios.post('/api/sections/delete', { id: this.section.id });
        this.deleteDialog = false;
        this.$router.push('/sections');
      } catch (e) {
        this.deleteError = e.response?.data?.message || 'Fehler beim Löschen.';
      } finally {
        this.deleting = false;
      }
    },

    // ---- Code-Editor (Monaco) ----
    detectLanguage(filename) {
      const ext = (filename || '').split('.').pop()?.toLowerCase();
      return ({
        liquid: 'html',
        html: 'html', htm: 'html',
        css: 'css', scss: 'scss', less: 'less',
        js: 'javascript', mjs: 'javascript', cjs: 'javascript',
        ts: 'typescript', tsx: 'typescript',
        json: 'json',
      })[ext] || 'plaintext';
    },
    openCodeEditor(type, target) {
      const ce = this.codeEditor;
      ce.type = type;
      ce.target = target;
      ce.saving = false;

      if (type === 'liquid') {
        ce.title = 'Liquid Code';
        ce.filePath = `sections/${this.section.handle || this.section.id}.liquid`;
        ce.content = this.uploadedLiquidFile?.liquidCode || '';
        ce.language = 'html';
        ce.icon = 'mdi-code-braces';
        ce.iconColor = '#2E7D32';
        ce.iconBg = 'rgba(46,125,50,0.1)';
      } else if (type === 'snippet') {
        ce.title = 'Snippet';
        ce.filePath = `snippets/${target.fileName}`;
        ce.content = target.snippetCode || '';
        ce.language = 'html';
        ce.icon = 'mdi-code-tags';
        ce.iconColor = '#7C4DFF';
        ce.iconBg = 'rgba(124,77,255,0.1)';
      } else if (type === 'block') {
        ce.title = 'Block';
        ce.filePath = `blocks/${target.fileName}`;
        ce.content = target.blockCode || '';
        ce.language = 'html';
        ce.icon = 'mdi-cube-outline';
        ce.iconColor = '#E65100';
        ce.iconBg = 'rgba(255,152,0,0.1)';
      } else if (type === 'asset') {
        ce.title = 'Asset';
        ce.filePath = target.filePath || `assets/${target.fileName}`;
        ce.content = target.fileCode || '';
        ce.language = this.detectLanguage(target.fileName || target.filePath);
        ce.icon = 'mdi-folder-multiple-outline';
        ce.iconColor = '#1565C0';
        ce.iconBg = 'rgba(21,101,192,0.1)';
      }
      ce.open = true;
    },

    async onCodeEditorSave(newContent) {
      const ce = this.codeEditor;
      ce.saving = true;
      try {
        if (ce.type === 'liquid') {
          // Über existierenden Files-Endpoint speichern (multipart wie beim Upload)
          const blob = new Blob([newContent], { type: 'text/plain' });
          const file = new File([blob], `${this.section.handle || this.section.id}.liquid`, { type: 'text/plain' });
          const fd = new FormData();
          fd.append('file', file);
          fd.append('sectionId', this.section.id);
          fd.append('codeType', 'liquid');
          await axios.post('/api/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          // section.liquidCode lokal aktualisieren — loadUploadedLiquid() UND
          // syncInfoFor('liquid') lesen daraus. Ohne diese Zeile blieben beide
          // stale, der Sync-Check verglich alten Inhalt → "synchron" obwohl
          // geändert. Erst ein Reload hätte section frisch geladen.
          this.section.liquidCode = newContent;
          await this.loadUploadedLiquid();
        } else if (ce.type === 'snippet') {
          const blob = new Blob([newContent], { type: 'text/plain' });
          const file = new File([blob], ce.target.fileName, { type: 'text/plain' });
          const fd = new FormData();
          fd.append('file', file);
          fd.append('id', ce.target.id);
          fd.append('fileName', ce.target.fileName);
          await axios.post('/api/sections/snippets/update', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          await this.loadUploadedSnippets();
        } else if (ce.type === 'block') {
          const blob = new Blob([newContent], { type: 'text/plain' });
          const file = new File([blob], ce.target.fileName, { type: 'text/plain' });
          const fd = new FormData();
          fd.append('file', file);
          fd.append('id', ce.target.id);
          fd.append('fileName', ce.target.fileName);
          await axios.post('/api/sections/blocks/update', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          await this.loadUploadedBlocks();
        } else if (ce.type === 'asset') {
          const blob = new Blob([newContent], { type: 'text/plain' });
          const file = new File([blob], ce.target.fileName || 'asset', { type: 'text/plain' });
          const fd = new FormData();
          fd.append('file', file);
          fd.append('id', ce.target.id);
          if (ce.target.filePath) fd.append('filePath', ce.target.filePath);
          await axios.post('/api/sections/assets/update', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          await this.loadUploadedAssets();
        }
        // Editor-Content nach Save aktualisieren (für dirty-Check)
        ce.content = newContent;
        // GitHub-Sync-Status auffrischen → Datei zeigt jetzt "different" und kann gepusht werden
        this.refreshAllSyncStatuses();
        this.showToast('success', 'Gespeichert', `${ce.filePath || ce.title} wurde aktualisiert`);
      } catch (e) {
        console.error('Code-Editor speichern fehlgeschlagen:', e);
        this.showToast('error', 'Fehler beim Speichern', e.response?.data?.message || 'Bitte erneut versuchen');
      } finally {
        ce.saving = false;
      }
    },

    // ---- Vorhandene Anleitung aus Library auswählen ----
    // ---- Verknüpfte Anleitungen (n:m) ----
    async loadLinkedGuides() {
      if (!this.section?.id) return;
      this.loadingLinkedGuides = true;
      try {
        const res = await axios.get('/api/sections/linked-guides', {
          params: { sectionId: this.section.id },
        });
        this.linkedGuides = res.data?.data || [];
      } catch (e) {
        console.error('Fehler beim Laden der verknüpften Anleitungen:', e);
        this.linkedGuides = [];
      } finally {
        this.loadingLinkedGuides = false;
      }
    },
    contentPreviewText(g) {
      const html = g.contentPreview || g.content || '';
      if (!html) return '';
      const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      return text.length > 120 ? text.substring(0, 120).trimEnd() + '…' : text;
    },
    isGuideLinked(guideId) {
      return this.linkedGuides.some((g) => g.id === guideId);
    },

    async openGuideViewer(g) {
      this.guideViewer = { open: true, loading: true, title: g.title, content: '' };
      try {
        const res = await axios.get('/api/guides/get', { params: { id: g.id } });
        this.guideViewer.content = res.data?.data?.content || '<p style="color:#888">Diese Anleitung hat noch keinen Inhalt.</p>';
      } catch (e) {
        this.guideViewer.content = '<p style="color:#D32F2F">Anleitung konnte nicht geladen werden.</p>';
      } finally {
        this.guideViewer.loading = false;
      }
    },

    async openPickGuideDialog() {
      this.pickGuideDialog = true;
      this.pickGuideSearch = '';
      this.pickedGuideIds = [];
      this.pickGuideError = '';
      this.loadingGuideLibrary = true;
      try {
        const res = await axios.get('/api/guides/get');
        this.guideLibrary = res.data?.data || [];
      } catch (e) {
        this.pickGuideError = 'Anleitungen konnten nicht geladen werden.';
      } finally {
        this.loadingGuideLibrary = false;
      }
    },
    togglePickGuide(id) {
      if (this.isGuideLinked(id)) return;   // bereits verknüpfte sind nicht wählbar
      const i = this.pickedGuideIds.indexOf(id);
      if (i === -1) this.pickedGuideIds.push(id);
      else this.pickedGuideIds.splice(i, 1);
    },
    async applyPickedGuides() {
      if (!this.pickedGuideIds.length) return;
      this.pickingGuide = true;
      this.pickGuideError = '';
      try {
        await axios.post('/api/sections/link-guides', {
          sectionId: this.section.id,
          guideIds: this.pickedGuideIds,
        });
        const n = this.pickedGuideIds.length;
        this.pickGuideDialog = false;
        await this.loadLinkedGuides();
        this.guideMessage = `${n} Anleitung${n === 1 ? '' : 'en'} verknüpft.`;
        this.guideSuccess = true;
        setTimeout(() => { this.guideMessage = ''; }, 3500);
      } catch (e) {
        this.pickGuideError = e.response?.data?.message || 'Fehler beim Verknüpfen.';
      } finally {
        this.pickingGuide = false;
      }
    },

    handleOutsideClick(e) {
      if (this.categoryMenuOpen) {
        const el = this.$refs.categoryDropdown;
        if (el && !el.contains(e.target)) this.categoryMenuOpen = false;
      }
      if (this.purposeMenuOpen) {
        const el = this.$refs.purposeDropdown;
        if (el && !el.contains(e.target)) this.purposeMenuOpen = false;
      }
      if (this.moreMenuOpen) {
        const el = this.$refs.moreMenuRef;
        if (el && !el.contains(e.target)) this.moreMenuOpen = false;
      }
    },

    async reloadAll(initial = false) {
      this.loading = true;
      // States für die neue Section zurücksetzen
      this.section = {};
      this.uploadedLiquidFile = null;
      this.uploadedSnippets = [];
      this.uploadedAssets = [];
      this.uploadedBlocks = [];
      this.syncStatuses = {};
      try {
        await Promise.all([this.loadCategories(), this.loadAvailableChannels()]);
        await this.getCurrentSection();
        await Promise.all([
          this.loadUploadedLiquid(),
          this.loadUploadedSnippets(),
          this.loadUploadedAssets(),
          this.loadUploadedBlocks(),
          this.loadLinkedGuides(),
        ]);
        if (initial || this.allSectionIds.length === 0) {
          await this.loadAllSectionIds();
          await this.loadGithubStatus();
        } else {
          this.refreshAllSyncStatuses();
        }
      } catch (e) {
        console.error('Fehler beim Laden der Section:', e);
      } finally {
        this.loading = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },

    async loadAllSectionIds() {
      try {
        const { data } = await axios.get('/api/sections/get');
        this.allSectionIds = Array.isArray(data) ? data.map((s) => s.id) : [];
      } catch (e) {
        console.error('Fehler beim Laden der Section-IDs:', e);
        this.allSectionIds = [];
      }
    },

    async loadAvailableChannels() {
      try {
        const { data } = await axios.get('/api/sections/sales-channels/available');
        this.availableChannels = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error('Fehler beim Laden der Vertriebskanäle:', e);
        this.availableChannels = [];
      }
    },

    hasChannel(id) {
      return Array.isArray(this.section.salesChannels) && this.section.salesChannels.includes(id);
    },

    async toggleChannel(id) {
      if (this.section.status !== 'completed') {
        this.channelMessage = 'Section muss zuerst auf Fertiggestellt stehen.';
        return;
      }
      this.channelMessage = '';
      const current = Array.isArray(this.section.salesChannels) ? [...this.section.salesChannels] : [];
      const next = current.includes(id) ? current.filter((c) => c !== id) : [...current, id];
      try {
        const { data } = await axios.post('/api/sections/sales-channels/update', {
          id: this.section.id,
          channels: next,
        });
        if (data && data.success) {
          this.section = { ...this.section, salesChannels: data.channels || [] };
          this.originalSection = { ...this.originalSection, salesChannels: data.channels || [] };
          this.showToast('success', 'Vertriebskanal aktualisiert');
        } else {
          throw new Error(data?.message || 'Unbekannter Fehler');
        }
      } catch (e) {
        console.error('Fehler beim Aktualisieren des Vertriebskanals:', e);
        this.channelMessage = e.response?.data?.message || 'Fehler beim Aktualisieren des Vertriebskanals';
      }
    },

    async setStatus(newStatus) {
      if (this.section.status === newStatus) return;
      // Wenn von completed → draft, warnen falls Channels gesetzt sind
      if (
        newStatus === 'draft' &&
        this.section.status === 'completed' &&
        Array.isArray(this.section.salesChannels) &&
        this.section.salesChannels.length > 0
      ) {
        const ok = window.confirm(
          'Wenn du den Status auf »Entwurf« zurücksetzt, werden alle Vertriebskanäle entfernt. Fortfahren?'
        );
        if (!ok) return;
      }
      this.section.status = newStatus;
    },

    selectPurpose(value) {
      this.section.purpose = value;
      this.purposeMenuOpen = false;
    },

    goToSection(id) {
      if (!id) return;
      this.$router.push(`/section/${id}`);
    },

    openNewGuideEditor() {
      this.guideMessage = '';
      this.guideEditorMode = 'new';
      this.guideEditorId = null;
      this.guideEditorTitle = this.section.title ? `Anleitung: ${this.section.title}` : '';
      this.guideEditorContent = '';
      this.guideEditorOpen = true;
    },

    async openEditGuideEditor(g) {
      this.guideMessage = '';
      this.guideEditorMode = 'edit';
      this.guideEditorId = g.id;
      this.guideEditorTitle = g.title || '';
      this.guideEditorContent = '';
      try {
        const res = await axios.get('/api/guides/get', { params: { id: g.id } });
        this.guideEditorContent = res.data?.data?.content || '';
      } catch (e) {
        this.guideMessage = 'Anleitung konnte nicht geladen werden.';
        this.guideSuccess = false;
        return;
      }
      this.guideEditorOpen = true;
    },

    // payload = { title, content } (vom GuideEditor)
    async saveGuide(payload) {
      this.savingGuide = true;
      this.guideMessage = '';
      try {
        if (this.guideEditorMode === 'new') {
          // Neue Library-Anleitung anlegen UND direkt mit dieser Section verknüpfen
          await axios.post('/api/guides/create', {
            title: payload.title,
            content: payload.content,
            sectionIds: [this.section.id],
          });
        } else {
          await axios.post('/api/guides/update', {
            id: this.guideEditorId,
            title: payload.title,
            content: payload.content,
          });
        }
        this.guideEditorOpen = false;
        await this.loadLinkedGuides();
        this.guideMessage = this.guideEditorMode === 'new'
          ? 'Anleitung angelegt & verknüpft'
          : 'Anleitung gespeichert';
        this.guideSuccess = true;
        setTimeout(() => { this.guideMessage = ''; }, 3000);
      } catch (e) {
        console.error('Fehler beim Speichern der Anleitung:', e);
        this.guideMessage = e.response?.data?.message || 'Fehler beim Speichern der Anleitung';
        this.guideSuccess = false;
      } finally {
        this.savingGuide = false;
      }
    },

    openUnlinkGuideDialog(g) {
      this.unlinkGuideTarget = g;
      this.unlinkGuideError = '';
      this.unlinkGuideDialog = true;
    },
    async confirmUnlinkGuide() {
      if (!this.unlinkGuideTarget) return;
      this.unlinkingGuide = true;
      this.unlinkGuideError = '';
      try {
        await axios.post('/api/sections/unlink-guide', {
          sectionId: this.section.id,
          guideId: this.unlinkGuideTarget.id,
        });
        this.unlinkGuideDialog = false;
        await this.loadLinkedGuides();
        this.guideMessage = 'Anleitung von Section getrennt';
        this.guideSuccess = true;
        setTimeout(() => { this.guideMessage = ''; }, 3000);
      } catch (e) {
        console.error('Fehler beim Trennen der Anleitung:', e);
        this.unlinkGuideError = e.response?.data?.message || 'Fehler beim Trennen der Anleitung';
      } finally {
        this.unlinkingGuide = false;
      }
    },

    selectCategory(cat) {
      this.section.categoryId = cat.id;
      this.categoryMenuOpen = false;
    },

    async getCurrentSection() {
      try {
        const result = await axios.get('/api/sections/get', {
          params: { id: this.$route.params.id },
        });
        const sec = result.data || {};
        sec.salesChannels = Array.isArray(sec.salesChannels) ? sec.salesChannels : [];
        sec.purpose = sec.purpose || null;
        sec.status = sec.status || 'draft';
        this.section = sec;
        this.originalSection = { ...sec, salesChannels: [...sec.salesChannels] };
      } catch (e) {
        console.error('Fehler beim Laden!', e);
      }
    },

    async loadCategories() {
      if (this.categoriesLoaded || this.loadingCategories) return;
      this.loadingCategories = true;
      try {
        const result = await axios.get('/api/sections/categories');
        this.categories = result.data;
        this.categoriesLoaded = true;
      } catch (e) {
        console.error('Fehler beim Laden der Kategorien!', e);
      } finally {
        this.loadingCategories = false;
      }
    },

    async saveChanges() {
      this.saving = true;
      this.saveMessage = '';
      try {
        const isFree = this.section.price <= 0 ? 1 : 0;
        const response = await axios.post('/api/sections/update', {
          id: this.section.id,
          categoryId: this.section.categoryId,
          title: this.section.title,
          demoStore: this.section.demoStore,
          price: this.section.price || 0,
          isFree,
          status: this.section.status,
          purpose: this.section.purpose === null ? '' : this.section.purpose,
        });
        this.saveMessage = 'Änderungen erfolgreich gespeichert!';
        this.saveSuccess = true;
        if (response.data.section) {
          const sec = response.data.section;
          sec.salesChannels = Array.isArray(sec.salesChannels) ? sec.salesChannels : [];
          this.section = { ...this.section, ...sec };
          this.originalSection = { ...this.section, salesChannels: [...this.section.salesChannels] };
        } else {
          this.originalSection = { ...this.section, salesChannels: [...(this.section.salesChannels || [])] };
        }
        setTimeout(() => { this.saveMessage = ''; }, 3000);
      } catch (error) {
        console.error('Fehler beim Speichern:', error);
        this.saveMessage = error.response?.data?.message || 'Fehler beim Speichern der Änderungen!';
        this.saveSuccess = false;
      } finally {
        this.saving = false;
      }
    },

    async loadUploadedLiquid() {
      this.loadingLiquid = true;
      try {
        if (this.section.liquidCode) {
          this.uploadedLiquidFile = {
            handle: this.section.handle,
            liquidCode: this.section.liquidCode,
            size: new Blob([this.section.liquidCode]).size,
          };
        } else this.uploadedLiquidFile = null;
      } catch (e) {
        console.error('Fehler beim Laden des Liquid Codes!', e);
        this.uploadedLiquidFile = null;
      } finally {
        this.loadingLiquid = false;
      }
    },

    async loadUploadedSnippets() {
      this.loadingSnippets = true;
      try {
        const result = await axios.get('/api/sections/snippets/get', {
          params: { sectionId: this.$route.params.id },
        });
        if (result.data.success) this.uploadedSnippets = result.data.data;
      } catch (e) { console.error('Fehler beim Laden der Snippets!', e); }
      finally { this.loadingSnippets = false; }
    },

    async loadUploadedAssets() {
      this.loadingAssets = true;
      try {
        const result = await axios.get('/api/sections/assets/get', {
          params: { sectionId: this.$route.params.id },
        });
        if (result.data.success) this.uploadedAssets = result.data.data;
      } catch (e) { console.error('Fehler beim Laden der Assets!', e); }
      finally { this.loadingAssets = false; }
    },

    async loadUploadedBlocks() {
      this.loadingBlocks = true;
      try {
        const result = await axios.get('/api/sections/blocks/get', {
          params: { sectionId: this.$route.params.id },
        });
        if (result.data.success) this.uploadedBlocks = result.data.data;
      } catch (e) { console.error('Fehler beim Laden der Blocks!', e); }
      finally { this.loadingBlocks = false; }
    },

    handleLiquidFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        if (file.name.endsWith('.liquid')) {
          this.selectedLiquidFile = file;
          this.liquidUploadMessage = '';
        } else {
          this.liquidUploadMessage = 'Bitte nur .liquid-Dateien hochladen!';
          this.liquidUploadSuccess = false;
          this.selectedLiquidFile = null;
        }
      }
    },

    clearLiquidFile() {
      this.selectedLiquidFile = null;
      this.liquidUploadMessage = '';
      this.$refs.liquidFileInput.value = '';
    },

    async uploadLiquidFile() {
      if (!this.selectedLiquidFile) return;
      this.uploadingLiquid = true;
      this.liquidUploadMessage = '';
      try {
        const formData = new FormData();
        formData.append('file', this.selectedLiquidFile);
        formData.append('sectionId', this.section.id);
        formData.append('handle', this.section.handle);
        formData.append('codeType', 'liquid');
        await axios.post('/api/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.liquidUploadMessage = 'Liquid Code erfolgreich hochgeladen!';
        this.liquidUploadSuccess = true;
        await this.getCurrentSection();
        await this.loadUploadedLiquid();
        this.refreshAllSyncStatuses();
        setTimeout(() => this.clearLiquidFile(), 2000);
      } catch (error) {
        console.error('Upload Fehler:', error);
        this.liquidUploadMessage = error.response?.data?.message || 'Fehler beim Hochladen der Datei!';
        this.liquidUploadSuccess = false;
      } finally {
        this.uploadingLiquid = false;
      }
    },

    downloadLiquidCode() {
      if (!this.uploadedLiquidFile?.liquidCode) return;
      try {
        const blob = new Blob([this.uploadedLiquidFile.liquidCode], { type: 'text/plain;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.uploadedLiquidFile.handle}.liquid`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download Fehler:', error);
      }
    },

    async deleteLiquidCode() {
      if (!confirm('Möchtest du den Liquid Code wirklich löschen?')) return;
      const path = `sections/${this.section.handle}.liquid`;
      try {
        await axios.post('/api/files/delete', { sectionId: this.section.id, codeType: 'liquid' });
        this.uploadedLiquidFile = null;
        this.liquidUploadMessage = 'Liquid Code erfolgreich gelöscht!';
        this.liquidUploadSuccess = true;
        await this.getCurrentSection();
        await this.maybeDeleteInRepo(path);
        setTimeout(() => { this.liquidUploadMessage = ''; }, 2000);
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
      }
    },

    handleSnippetFileSelect(event) {
      const files = Array.from(event.target.files);
      files.forEach((file) => {
        if (file.name.endsWith('.liquid')) {
          this.selectedSnippets.push({ file, name: file.name, size: file.size });
        }
      });
      this.$refs.snippetFileInput.value = '';
    },

    removeSelectedSnippet(index) { this.selectedSnippets.splice(index, 1); },

    async uploadSnippets() {
      if (this.selectedSnippets.length === 0) return;
      this.uploadingSnippets = true;
      this.snippetUploadMessage = '';
      let successCount = 0, errorCount = 0;
      for (const snippet of this.selectedSnippets) {
        try {
          const formData = new FormData();
          formData.append('file', snippet.file);
          formData.append('sectionId', this.section.id);
          formData.append('fileName', snippet.name);
          await axios.post('/api/sections/snippets/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          successCount++;
        } catch (error) { console.error('Upload Fehler:', error); errorCount++; }
      }
      if (successCount > 0) {
        this.snippetUploadMessage = `${successCount} Snippet(s) erfolgreich hochgeladen!`;
        this.snippetUploadSuccess = true;
        this.selectedSnippets = [];
        await this.loadUploadedSnippets();
        this.refreshAllSyncStatuses();
      }
      if (errorCount > 0) {
        this.snippetUploadMessage += ` ${errorCount} Fehler aufgetreten.`;
        this.snippetUploadSuccess = false;
      }
      this.uploadingSnippets = false;
      setTimeout(() => { this.snippetUploadMessage = ''; }, 3000);
    },

    async downloadSnippet(snippet) {
      try {
        const response = await axios.get(`/api/sections/snippets/download/${snippet.id}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = snippet.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) { console.error('Download Fehler:', error); }
    },

    async deleteSnippet(snippet) {
      const snippetId = typeof snippet === 'object' ? snippet.id : snippet;
      const fileName = typeof snippet === 'object' ? snippet.fileName : null;
      if (!confirm('Möchtest du dieses Snippet wirklich löschen?')) return;
      try {
        await axios.post('/api/sections/snippets/delete', { id: snippetId });
        this.snippetUploadMessage = 'Snippet erfolgreich gelöscht!';
        this.snippetUploadSuccess = true;
        await this.loadUploadedSnippets();
        if (fileName) await this.maybeDeleteInRepo(`snippets/${fileName}`);
        setTimeout(() => { this.snippetUploadMessage = ''; }, 2000);
      } catch (error) { console.error('Fehler beim Löschen:', error); }
    },

    handleBlockFileSelect(event) {
      const files = Array.from(event.target.files);
      files.forEach((file) => {
        if (file.name.endsWith('.liquid')) {
          this.selectedBlocks.push({ file, name: file.name, size: file.size });
        }
      });
      this.$refs.blockFileInput.value = '';
    },

    removeSelectedBlock(index) { this.selectedBlocks.splice(index, 1); },

    async uploadBlocks() {
      if (this.selectedBlocks.length === 0) return;
      this.uploadingBlocks = true;
      this.blockUploadMessage = '';
      let successCount = 0, errorCount = 0;
      for (const block of this.selectedBlocks) {
        try {
          const formData = new FormData();
          formData.append('file', block.file);
          formData.append('sectionId', this.section.id);
          formData.append('fileName', block.name);
          await axios.post('/api/sections/blocks/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          successCount++;
        } catch (error) { console.error('Upload Fehler:', error); errorCount++; }
      }
      if (successCount > 0) {
        this.blockUploadMessage = `${successCount} Block(s) erfolgreich hochgeladen!`;
        this.blockUploadSuccess = true;
        this.selectedBlocks = [];
        await this.loadUploadedBlocks();
        this.refreshAllSyncStatuses();
      }
      if (errorCount > 0) {
        this.blockUploadMessage += ` ${errorCount} Fehler aufgetreten.`;
        this.blockUploadSuccess = false;
      }
      this.uploadingBlocks = false;
      setTimeout(() => { this.blockUploadMessage = ''; }, 3000);
    },

    async downloadBlock(block) {
      try {
        const response = await axios.get(`/api/sections/blocks/download/${block.id}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = block.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) { console.error('Download Fehler:', error); }
    },

    async deleteBlock(block) {
      const blockId = typeof block === 'object' ? block.id : block;
      const fileName = typeof block === 'object' ? block.fileName : null;
      if (!confirm('Möchtest du diesen Block wirklich löschen?')) return;
      try {
        await axios.post('/api/sections/blocks/delete', { id: blockId });
        this.blockUploadMessage = 'Block erfolgreich gelöscht!';
        this.blockUploadSuccess = true;
        await this.loadUploadedBlocks();
        if (fileName) await this.maybeDeleteInRepo(`blocks/${fileName}`);
        setTimeout(() => { this.blockUploadMessage = ''; }, 2000);
      } catch (error) { console.error('Fehler beim Löschen:', error); }
    },

    handleImageFileSelect(event) {
      const file = event.target.files[0];
      if (file && file.type.startsWith('image/')) {
        this.selectedImageFile = file;
        this.uploadImageFile();
      } else {
        this.imageUploadMessage = 'Bitte nur Bilddateien hochladen!';
        this.imageUploadSuccess = false;
        this.selectedImageFile = null;
      }
    },

    // Skaliert + komprimiert ein Bild clientseitig auf eine kleine, konsistente
    // Größe. Hintergrund: Der Produktiv-Proxy (nginx) lehnt Requests über
    // ~1 MB mit HTTP 413 ab — große Originalbilder schlugen darum mal durch,
    // mal nicht. Nach der Komprimierung bleibt das Bild zuverlässig klein.
    async compressImage(file) {
      const MAX_EDGE = 1600;
      const TARGET_BYTES = 900 * 1024;
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(file);
        });
        const img = await new Promise((resolve, reject) => {
          const i = new Image();
          i.onload = () => resolve(i);
          i.onerror = reject;
          i.src = dataUrl;
        });

        const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        // Weißer Hintergrund — JPEG kennt keine Transparenz; auf den hellen
        // Karten der App ist Weiß ohnehin der richtige Hintergrund.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);

        const encode = (q) => new Promise((resolve) => {
          canvas.toBlob((b) => resolve(b), 'image/jpeg', q);
        });

        let quality = 0.85;
        let blob = await encode(quality);
        while (blob && blob.size > TARGET_BYTES && quality > 0.4) {
          quality -= 0.15;
          const next = await encode(quality);
          if (!next) break;
          blob = next;
        }
        return blob || file;
      } catch (e) {
        console.warn('Bild-Komprimierung fehlgeschlagen, nutze Original:', e);
        return file;
      }
    },

    async uploadImageFile() {
      if (!this.selectedImageFile) return;
      this.uploadingImage = true;
      this.imageUploadMessage = '';
      try {
        const compressed = await this.compressImage(this.selectedImageFile);
        const formData = new FormData();
        formData.append('image', compressed, 'preview.jpg');
        formData.append('id', this.section.id);
        await axios.post('/api/sections/update-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.imageUploadMessage = 'Bild erfolgreich aktualisiert!';
        this.imageUploadSuccess = true;
        this.imageKey = Date.now();
        setTimeout(() => {
          this.imageUploadMessage = '';
          this.selectedImageFile = null;
          this.$refs.imageFileInput.value = '';
        }, 2000);
      } catch (error) {
        console.error('Upload Fehler:', error);
        this.imageUploadMessage = error.response?.data?.message || 'Fehler beim Hochladen des Bildes!';
        this.imageUploadSuccess = false;
      } finally {
        this.uploadingImage = false;
      }
    },

    handleAssetFileSelect(event) {
      const files = Array.from(event.target.files);
      files.forEach((file) => {
        const extension = file.name.split('.').pop().toLowerCase();
        let defaultPath = `assets/${file.name}`;
        if (['liquid'].includes(extension)) defaultPath = `snippets/${file.name}`;
        this.selectedAssets.push({
          file, name: file.name, size: file.size, filePath: defaultPath, fileType: file.type || extension,
        });
      });
      this.$refs.assetFileInput.value = '';
    },

    removeSelectedAsset(index) { this.selectedAssets.splice(index, 1); },

    async uploadAssets() {
      if (this.selectedAssets.length === 0) return;
      this.uploadingAssets = true;
      this.assetUploadMessage = '';
      let successCount = 0, errorCount = 0;
      for (const asset of this.selectedAssets) {
        try {
          const formData = new FormData();
          formData.append('file', asset.file);
          formData.append('sectionId', this.section.id);
          formData.append('fileName', asset.name);
          formData.append('fileType', asset.fileType);
          formData.append('filePath', asset.filePath);
          await axios.post('/api/sections/assets/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          successCount++;
        } catch (error) { console.error('Upload Fehler:', error); errorCount++; }
      }
      if (successCount > 0) {
        this.assetUploadMessage = `${successCount} Asset(s) erfolgreich hochgeladen!`;
        this.assetUploadSuccess = true;
        this.selectedAssets = [];
        await this.loadUploadedAssets();
        this.refreshAllSyncStatuses();
      }
      if (errorCount > 0) {
        this.assetUploadMessage += ` ${errorCount} Fehler aufgetreten.`;
        this.assetUploadSuccess = false;
      }
      this.uploadingAssets = false;
      setTimeout(() => { this.assetUploadMessage = ''; }, 3000);
    },

    async downloadAsset(asset) {
      try {
        const response = await axios.get(`/api/sections/assets/download/${asset.id}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = asset.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) { console.error('Download Fehler:', error); }
    },

    async deleteAsset(asset) {
      const assetId = typeof asset === 'object' ? asset.id : asset;
      const filePath = typeof asset === 'object' ? asset.filePath : null;
      if (!confirm('Möchtest du dieses Asset wirklich löschen?')) return;
      try {
        await axios.post('/api/sections/assets/delete', { id: assetId });
        this.assetUploadMessage = 'Asset erfolgreich gelöscht!';
        this.assetUploadSuccess = true;
        await this.loadUploadedAssets();
        if (filePath) await this.maybeDeleteInRepo(filePath);
        setTimeout(() => { this.assetUploadMessage = ''; }, 2000);
      } catch (error) { console.error('Fehler beim Löschen:', error); }
    },

    getAssetIcon(fileName) {
      const ext = fileName.split('.').pop().toLowerCase();
      const map = {
        css: 'mdi-language-css3', js: 'mdi-language-javascript', liquid: 'mdi-water',
        json: 'mdi-code-json', html: 'mdi-language-html5',
        png: 'mdi-file-image', jpg: 'mdi-file-image', jpeg: 'mdi-file-image',
        gif: 'mdi-file-image', webp: 'mdi-file-image',
        svg: 'mdi-svg', mp4: 'mdi-file-video', webm: 'mdi-file-video', ogg: 'mdi-file-video',
        woff: 'mdi-format-font', woff2: 'mdi-format-font', ttf: 'mdi-format-font',
        otf: 'mdi-format-font', eot: 'mdi-format-font',
      };
      return map[ext] || 'mdi-file-outline';
    },

    assetColor(fileName) {
      const ext = fileName.split('.').pop().toLowerCase();
      const map = {
        css: '#1565C0', js: '#F57F17', liquid: '#7C4DFF', json: '#2E7D32', html: '#E65100',
        png: '#00838F', jpg: '#00838F', jpeg: '#00838F', gif: '#00838F', webp: '#00838F',
        svg: '#5B5BD6', mp4: '#D32F2F', webm: '#D32F2F', ogg: '#D32F2F',
        woff: '#C2185B', woff2: '#C2185B', ttf: '#C2185B', otf: '#C2185B', eot: '#C2185B',
      };
      return map[ext] || '#666';
    },

    assetBg(fileName) {
      const ext = fileName.split('.').pop().toLowerCase();
      const map = {
        css: 'rgba(21,101,192,0.1)', js: 'rgba(245,127,23,0.1)', liquid: 'rgba(124,77,255,0.1)',
        json: 'rgba(46,125,50,0.1)', html: 'rgba(255,152,0,0.1)',
        png: 'rgba(0,131,143,0.1)', jpg: 'rgba(0,131,143,0.1)', jpeg: 'rgba(0,131,143,0.1)',
        gif: 'rgba(0,131,143,0.1)', webp: 'rgba(0,131,143,0.1)',
        svg: 'rgba(91,91,214,0.1)',
        mp4: 'rgba(211,47,47,0.1)', webm: 'rgba(211,47,47,0.1)', ogg: 'rgba(211,47,47,0.1)',
        woff: 'rgba(194,24,91,0.1)', woff2: 'rgba(194,24,91,0.1)',
        ttf: 'rgba(194,24,91,0.1)', otf: 'rgba(194,24,91,0.1)', eot: 'rgba(194,24,91,0.1)',
      };
      return map[ext] || '#f0f2f5';
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    },
    formatBytes(n) {
      if (n === null || n === undefined) return '—';
      return this.formatFileSize(Number(n));
    },

    showToast(type, title, message = '', timeout = 3500) {
      this.toast = { show: true, type, title, message, timeout };
    },

    // ---------------- GITHUB SYNC ----------------
    async loadGithubStatus() {
      try {
        const { data } = await axios.get('/api/integrations/github/status');
        this.githubStatus = data || { connected: false, repo: null };
        if (this.githubStatus.connected && this.githubStatus.repo) {
          this.refreshAllSyncStatuses();
        }
      } catch (e) {
        this.githubStatus = { connected: false, repo: null };
      }
    },

    syncKey(type, id) { return `${type}:${id}`; },
    syncStatusOf(type, id) { return this.syncStatuses[this.syncKey(type, id)]; },
    syncBadgeClass(type, id) {
      const s = this.syncStatusOf(type, id);
      if (s === 'identical') return 'sync-synced';
      if (s === 'different') return 'sync-dirty';
      if (s === 'not_in_repo') return 'sync-missing';
      return '';
    },
    syncBadgeTitle(type, id) {
      const s = this.syncStatusOf(type, id);
      if (s === 'identical') return 'Datei ist synchron mit dem Repo';
      if (s === 'different') return 'Datei unterscheidet sich vom Repo';
      if (s === 'not_in_repo') return 'Datei ist noch nicht im Repo';
      return 'Mit Repository synchronisieren';
    },

    async fetchSyncStatus(type, item) {
      const info = this.syncInfoFor(type, item);
      if (!info) return null;
      try {
        const { data } = await axios.post('/api/integrations/github/sync/check', {
          path: info.path,
          localContent: info.content,
        });
        return data.success ? data.status : null;
      } catch {
        return null;
      }
    },

    async refreshAllSyncStatuses() {
      if (!this.githubStatus.connected || !this.githubStatus.repo) return;
      const tasks = [];
      if (this.uploadedLiquidFile) {
        tasks.push(this.fetchSyncStatus('liquid', null).then((s) =>
          s && (this.syncStatuses = { ...this.syncStatuses, [this.syncKey('liquid', this.section.id)]: s })
        ));
      }
      for (const s of this.uploadedSnippets) {
        tasks.push(this.fetchSyncStatus('snippet', s).then((st) =>
          st && (this.syncStatuses = { ...this.syncStatuses, [this.syncKey('snippet', s.id)]: st })
        ));
      }
      for (const b of this.uploadedBlocks) {
        tasks.push(this.fetchSyncStatus('block', b).then((st) =>
          st && (this.syncStatuses = { ...this.syncStatuses, [this.syncKey('block', b.id)]: st })
        ));
      }
      for (const a of this.uploadedAssets) {
        // binäre Assets überspringen
        if (!a.fileCode && a.fileData) continue;
        tasks.push(this.fetchSyncStatus('asset', a).then((st) =>
          st && (this.syncStatuses = { ...this.syncStatuses, [this.syncKey('asset', a.id)]: st })
        ));
      }
      await Promise.allSettled(tasks);
    },

    setLocalSyncStatus(type, id, status) {
      this.syncStatuses = { ...this.syncStatuses, [this.syncKey(type, id)]: status };
    },

    // Pfad + Inhalt pro Datei-Typ bestimmen
    syncInfoFor(type, item) {
      if (type === 'liquid') {
        return { path: `sections/${this.section.handle}.liquid`, content: this.section.liquidCode || '' };
      }
      if (type === 'snippet') {
        return { path: `snippets/${item.fileName}`, content: item.snippetCode || '' };
      }
      if (type === 'block') {
        return { path: `blocks/${item.fileName}`, content: item.blockCode || '' };
      }
      if (type === 'asset') {
        if (!item.fileCode && item.fileData) {
          // Binärer Asset — aktuell nicht unterstützt
          alert('Binäre Assets können derzeit nicht per Git synchronisiert werden. Nur Text-Dateien (CSS, JS, Liquid, JSON, SVG).');
          return null;
        }
        return { path: item.filePath, content: item.fileCode || '' };
      }
      return null;
    },

    async startSync(type, item) {
      const info = this.syncInfoFor(type, item);
      if (!info) return;
      this.syncing = true;
      try {
        const { data } = await axios.post('/api/integrations/github/sync/check', {
          path: info.path,
          localContent: info.content,
        });
        if (!data.success) throw new Error(data.message || 'Fehler');
        const id = type === 'liquid' ? this.section.id : item?.id;
        if (id) this.setLocalSyncStatus(type, id, data.status);
        this.syncState = {
          status: data.status,
          path: data.path,
          localContent: data.localContent,
          remoteContent: data.remoteContent,
          sha: data.sha,
          type,
          target: item,
        };
        this.syncDialog = true;
      } catch (e) {
        this.showToast('error', 'Prüfung fehlgeschlagen', e.response?.data?.message || e.message || 'Unbekannter Fehler');
      } finally {
        this.syncing = false;
      }
    },

    closeSyncDialog() {
      this.syncDialog = false;
      this.syncDirection = null;
      this.syncState = { status: null, path: '', localContent: '', remoteContent: '', sha: null, type: null, target: null };
    },

    async executeSync(direction) {
      this.syncing = true;
      this.syncDirection = direction;
      const { type, target, path } = this.syncState;
      const id = type === 'liquid' ? this.section.id : target?.id;
      try {
        if (direction === 'push') {
          await axios.post('/api/integrations/github/sync/push', {
            path,
            content: this.syncState.localContent,
            sha: this.syncState.sha || null,
          });
          if (id) this.setLocalSyncStatus(type, id, 'identical');
          this.closeSyncDialog();
          this.showToast('success', 'Ins Repo gepusht', path);
        } else if (direction === 'pull') {
          const { data } = await axios.post('/api/integrations/github/sync/pull', { path });
          if (!data.success) throw new Error(data.message || 'Pull fehlgeschlagen');
          await this.writePulledContent(type, target, path, data.content);
          if (id) this.setLocalSyncStatus(type, id, 'identical');
          this.closeSyncDialog();
          this.showToast('success', 'Aus Repo übernommen', path);
        }
      } catch (e) {
        this.showToast('error', 'Sync fehlgeschlagen', e.response?.data?.message || e.message || 'Unbekannter Fehler');
      } finally {
        this.syncing = false;
        this.syncDirection = null;
      }
    },

    // Git-Content lokal ablegen (Dateiname/Pfad aus syncState)
    async writePulledContent(type, item, path, content) {
      const parts = path.split('/');
      const fileName = parts[parts.length - 1];
      const file = new File([content], fileName, { type: 'text/plain' });

      if (type === 'liquid') {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('sectionId', this.section.id);
        fd.append('handle', this.section.handle);
        fd.append('codeType', 'liquid');
        await axios.post('/api/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        await this.getCurrentSection();
        await this.loadUploadedLiquid();
      } else if (type === 'snippet') {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('sectionId', this.section.id);
        fd.append('fileName', fileName);
        await axios.post('/api/sections/snippets/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        await this.loadUploadedSnippets();
      } else if (type === 'block') {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('sectionId', this.section.id);
        fd.append('fileName', fileName);
        await axios.post('/api/sections/blocks/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        await this.loadUploadedBlocks();
      } else if (type === 'asset') {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('sectionId', this.section.id);
        fd.append('fileName', fileName);
        fd.append('fileType', (item && item.fileType) || 'text/plain');
        fd.append('filePath', path);
        await axios.post('/api/sections/assets/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        await this.loadUploadedAssets();
      }
    },

    bulkSyncConfirm() {
      this.bulkSyncDialog = true;
    },

    fileLabel(f) {
      if (f.type === 'liquid') return `${this.section.handle}.liquid`;
      if (f.type === 'snippet') return f.target?.fileName || '—';
      if (f.type === 'block') return f.target?.fileName || '—';
      if (f.type === 'asset') return f.target?.filePath || f.target?.fileName || '—';
      return '—';
    },
    fileTypeLabel(type) {
      return { liquid: 'Liquid', snippet: 'Snippet', block: 'Block', asset: 'Asset' }[type] || type;
    },
    fileTypeColor(type) {
      return { liquid: '#2E7D32', snippet: '#7C4DFF', block: '#E65100', asset: '#1565C0' }[type] || '#666';
    },
    fileTypeBg(type) {
      return {
        liquid: 'rgba(46,125,50,0.1)', snippet: 'rgba(124,77,255,0.1)',
        block: 'rgba(255,152,0,0.1)', asset: 'rgba(21,101,192,0.1)',
      }[type] || '#f0f2f5';
    },
    bulkActionColor(status) {
      if (status === 'different') return '#E65100';
      if (status === 'not_in_repo') return '#5B5BD6';
      return '#bbb';
    },
    bulkActionLabel(status) {
      if (status === 'different') return 'Überschreibt Repo-Version';
      if (status === 'not_in_repo') return 'Wird im Repo angelegt';
      return 'Wird gepusht';
    },

    async bulkSync() {
      this.bulkSyncDialog = false;
      this.bulkSyncing = true;
      const files = this.allSyncableFiles.filter((f) => this.syncStatusOf(f.type, f.id) !== 'identical');
      this.bulkSyncProgress = { current: 0, total: files.length };
      let errors = 0;
      for (const f of files) {
        this.bulkSyncProgress.current++;
        const info = this.syncInfoFor(f.type, f.target);
        if (!info) { errors++; continue; }
        try {
          // Aktuellen Remote-SHA holen falls Datei schon existiert (wir pushen mit Update-Safety)
          const check = await axios.post('/api/integrations/github/sync/check', {
            path: info.path,
            localContent: info.content,
          });
          const sha = check.data?.sha || null;
          await axios.post('/api/integrations/github/sync/push', {
            path: info.path,
            content: info.content,
            sha,
            message: `Bulk sync ${f.type} ${info.path} via SAM`,
          });
          this.setLocalSyncStatus(f.type, f.id, 'identical');
        } catch (e) {
          console.error('Bulk-Sync Fehler bei', info.path, e);
          errors++;
        }
      }
      this.bulkSyncing = false;
      this.bulkSyncProgress = { current: 0, total: 0 };
      if (errors === 0) {
        this.showToast('success', 'Alle Dateien synchronisiert', `${files.length} Datei(en) ins Repo gepusht`);
      } else if (errors === files.length) {
        this.showToast('error', 'Sync fehlgeschlagen', `Alle ${errors} Dateien konnten nicht gepusht werden`);
        this.refreshAllSyncStatuses();
      } else {
        this.showToast('warning', 'Teilweise synchronisiert', `${files.length - errors} erfolgreich · ${errors} Fehler`, 5000);
        this.refreshAllSyncStatuses();
      }
    },

    async maybeDeleteInRepo(path) {
      if (!this.githubStatus.connected || !this.githubStatus.repo) return;
      if (!confirm(`Soll die Datei auch im Repository (${this.githubStatus.repo.fullName}) gelöscht werden?\n\nPfad: ${path}`)) return;
      try {
        await axios.post('/api/integrations/github/sync/delete', { path });
        this.showToast('success', 'Im Repo gelöscht', path);
      } catch (e) {
        this.showToast('error', 'Löschen im Repo fehlgeschlagen', e.response?.data?.message || 'Unbekannter Fehler');
      }
    },
  },
};
</script>

<style scoped>
.page-title { font-size: 26px; font-weight: 800; color: #0a1628; letter-spacing: -0.3px; margin-bottom: 4px; line-height: 1.1; }
.page-subtitle { font-size: 13px; color: #888; }
.page-handle {
  display: inline-block;
  padding: 3px 10px; border-radius: 6px;
  background: rgba(10,22,40,0.06); color: #555;
  font-size: 12px; font-weight: 600;
  font-family: 'SFMono-Regular', Menlo, monospace;
}

/* Overall Sync Status (neben Titel) */
.sync-overall {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 12px; border-radius: 20px;
  font-size: 11px; font-weight: 700;
  line-height: 1;
  align-self: center;
  position: relative;
  top: 1px;
}
.overall-all-synced { background: rgba(46,125,50,0.1); color: #2E7D32; }
.overall-partial { background: rgba(255,152,0,0.12); color: #E65100; }
.overall-none-synced { background: rgba(211,47,47,0.08); color: #D32F2F; }
.overall-none { display: none; }

/* Compact Sync-All Button — gleiche Höhe wie secondary-btn, UI-Primary */
.btn-sync-all {
  padding: 10px 16px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  background: #0a1628 !important;
  color: white !important;
  border: 1px solid #0a1628 !important;
  border-radius: 10px !important;
  width: auto !important;
  white-space: nowrap;
}
.btn-sync-all:hover:not(:disabled) { background: #1d3457 !important; border-color: #1d3457 !important; }
.btn-sync-all:disabled { opacity: 0.5; cursor: not-allowed; }

/* Bulk-Sync Dialog — minimal */
.bulk-lead {
  font-size: 14px; color: #0a1628; line-height: 1.55;
  margin: 0 0 16px; padding: 0;
}
.bulk-lead strong { color: #0a1628; font-weight: 800; }
.bulk-warn { display: block; margin-top: 4px; font-size: 13px; color: #E65100; font-weight: 500; }

.bulk-list {
  display: flex; flex-direction: column; gap: 1px;
  background: #eceef2; border-radius: 10px; overflow: hidden;
  max-height: 280px; overflow-y: auto;
  border: 1px solid #eceef2;
}
.bulk-row {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 14px; background: white;
}
.bulk-row-dot {
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
}
.bulk-row-body { flex: 1; min-width: 0; }
.bulk-row-name {
  font-size: 13px; color: #0a1628; font-weight: 500;
  font-family: 'SFMono-Regular', Menlo, monospace;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.bulk-row-meta {
  font-size: 11px; color: #888; margin-top: 2px;
  display: flex; align-items: center; gap: 4px;
}
.bulk-row-meta > span:first-child { font-weight: 700; }
.bulk-row-sep { color: #ccc; }

.bulk-muted {
  font-size: 12px; color: #999; margin-top: 12px; font-style: italic;
}

/* Standard-Dialog (floowseo-Pattern, identisch wie in /categories) */
.dialog-backdrop {
  position: fixed; inset: 0; background: rgba(10,22,40,0.5);
  z-index: 2000; animation: fadeBg 0.2s ease;
}
@keyframes fadeBg { from { opacity: 0; } to { opacity: 1; } }
.modern-dialog {
  background: white !important;
  box-shadow: 0 24px 64px rgba(10,22,40,0.18) !important;
  z-index: 2001 !important;
  position: relative;
  animation: dlgSlideUp 0.3s cubic-bezier(0.16,1,0.3,1);
  overflow: visible !important;
}
@keyframes dlgSlideUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}
.close-btn {
  position: absolute; top: 16px; right: 16px; z-index: 10;
  width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 8px; cursor: pointer; color: #888;
  transition: all 0.2s;
}
.close-btn:hover { background: #f0f2f5; color: #0a1628; transform: rotate(90deg); }
.dh { display: flex; align-items: center; gap: 14px; padding: 28px 28px 0; }
.dh-icon {
  width: 44px; height: 44px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(10,22,40,0.06); flex-shrink: 0;
}
.dh-title { font-size: 18px; font-weight: 700; color: #0a1628; }
.dh-sub { font-size: 12px; color: #999; margin-top: 2px; }
.db { padding: 24px 28px; }
.dff {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 10px; padding: 4px 28px 24px;
}
.bp.btn-danger { background: #D32F2F; }
.bp.btn-danger:hover:not(:disabled) { background: #b71c1c; }

/* Toolbar-Löschen-Button (legacy, falls irgendwo noch genutzt) */
.secondary-btn.btn-delete {
  color: #D32F2F;
  border-color: #fecaca;
}
.secondary-btn.btn-delete:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #D32F2F;
}

/* 3-Dot Aktionsmenü in der Toolbar – gleiche Optik wie 'Nächste' & Co. */
.more-menu-wrap { position: relative; }
.secondary-btn.more-btn {
  color: #6b7280;
}
.secondary-btn.more-btn:hover { color: #0a1628; }
.secondary-btn.more-btn-open {
  background: #f0f2f5;
  border-color: #c5cbd3;
  color: #0a1628;
}
.more-menu {
  position: absolute; top: calc(100% + 6px); right: 0;
  min-width: 200px;
  background: white;
  border: 1px solid #eceef2;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(10,22,40,0.12);
  z-index: 60;
  padding: 6px;
  animation: moreFade 0.12s ease;
}
@keyframes moreFade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
.more-menu-item {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 8px 10px;
  background: transparent; border: none; border-radius: 7px;
  font-size: 13px; font-weight: 500; color: #0a1628;
  cursor: pointer; transition: background 0.1s; font-family: inherit;
  text-align: left;
}
.more-menu-item:hover { background: #fafbfc; }
.more-menu-item-danger { color: #D32F2F; }
.more-menu-item-danger:hover { background: rgba(211,47,47,0.06); }

.secondary-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 18px; background: white; color: #0a1628;
  border: 1px solid #eceef2; border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: border-color 0.15s; font-family: inherit;
}
.secondary-btn:hover:not(:disabled) { border-color: #0a1628; }
.secondary-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.grid-split { display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); gap: 20px; align-items: start; }
.col-left { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.files-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  min-width: 0;
}
.files-grid > .card-modern { min-width: 0; }
.col-right {
  display: flex; flex-direction: column; gap: 16px;
  position: sticky; top: 24px; min-width: 0;
}

.card-modern { background: white; border: 1px solid #eceef2; border-radius: 16px; padding: 24px; }

.section-title {
  font-size: 16px; font-weight: 700; color: #0a1628;
  display: flex; align-items: center; gap: 10px;
}
.title-icon {
  width: 30px; height: 30px; border-radius: 8px;
  background: rgba(10,22,40,0.06);
  display: flex; align-items: center; justify-content: center;
}
.title-count {
  margin-left: auto; padding: 3px 9px; border-radius: 10px;
  background: #f0f2f5; color: #666; font-size: 11px; font-weight: 700;
}

/* Form */
.fg { display: flex; flex-direction: column; position: relative; }
.fl { font-size: 11px; font-weight: 600; color: #999; margin-bottom: 6px; letter-spacing: 0.3px; }
.fl-hint { color: #bbb; font-weight: 400; text-transform: none; letter-spacing: 0; margin-left: 4px; }

.iw {
  display: flex; align-items: center; gap: 10px;
  height: 42px; padding: 0 14px; width: 100%;
  background: #f8f9fb; border: 1px solid #eceef2; border-radius: 10px;
  transition: all 0.15s;
}
.iw.iw-f { border-color: #0a1628; background: white; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.iw-trigger { cursor: pointer; text-align: left; font-family: inherit; background: white !important; }
.iw-trigger:hover:not(.iw-f) { border-color: #c5cbd3; }
.iw-readonly { opacity: 0.7; }
.ii {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 14px; color: #0a1628; font-weight: 500; min-width: 0;
  font-family: inherit; height: 40px;
}
.ii::placeholder { color: #bbb; font-weight: 400; }
.ii[readonly] { opacity: 0.7; cursor: default; }
.ii-val { flex: 1; font-size: 14px; color: #0a1628; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.chev-open { transform: rotate(180deg); transition: transform 0.2s; }

.combo-wrap { position: relative; }
.combo-menu {
  position: absolute; top: calc(100% + 6px); left: 0; right: 0;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  box-shadow: 0 12px 32px rgba(10,22,40,0.12);
  z-index: 50; max-height: 260px; overflow-y: auto;
  padding: 6px; animation: comboFade 0.12s ease;
}
@keyframes comboFade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
.combo-option {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 9px 10px; background: transparent; border: none;
  border-radius: 7px; cursor: pointer; transition: background 0.1s;
  text-align: left; font-family: inherit;
}
.combo-option:hover { background: #f8f9fb; }
.combo-label { flex: 1; font-size: 14px; color: #0a1628; font-weight: 500; }
.combo-empty { padding: 12px 14px; font-size: 13px; color: #999; text-align: center; }

/* Price Badge */
.price-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 0 14px; border-radius: 10px; font-size: 12px; font-weight: 700;
  white-space: nowrap; border: 1px solid transparent;
}
.badge-premium { background: rgba(255,152,0,0.1); color: #E65100; border-color: rgba(255,152,0,0.25); }
.badge-free { background: rgba(46,125,50,0.08); color: #2E7D32; border-color: rgba(46,125,50,0.2); }

/* Dropzone */
.dropzone {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 28px 16px; background: #fafbfc; border: 1px dashed #c5cbd3; border-radius: 12px;
  cursor: pointer; transition: all 0.15s; text-align: center;
}
.dropzone:hover { border-color: #0a1628; background: white; }
.dz-title { font-size: 13px; font-weight: 700; color: #0a1628; margin-bottom: 2px; }
.dz-sub { font-size: 11px; color: #888; }

/* File Rows */
.file-row {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px; background: #fafbfc;
  border: 1px solid #eceef2; border-radius: 10px;
  margin-top: 8px;
}
.file-row-pending { background: rgba(10,22,40,0.03); border-style: dashed; }
.file-icon {
  width: 36px; height: 36px; min-width: 36px; border-radius: 9px;
  background: #f0f2f5; display: flex; align-items: center; justify-content: center;
}
.file-body { flex: 1; min-width: 0; }
.file-name {
  font-size: 13px; font-weight: 600; color: #0a1628;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.file-meta {
  font-size: 11px; color: #888; margin-top: 2px;
  font-family: 'SFMono-Regular', Menlo, monospace;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.file-actions { display: flex; gap: 2px; }

.action-btn {
  width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: 1px solid transparent; border-radius: 7px;
  cursor: pointer; color: #666; transition: all 0.15s;
}
.action-btn:hover { background: #f0f2f5; color: #0a1628; }
.action-danger:hover { background: rgba(211,47,47,0.08); color: #D32F2F; }
.action-edit { color: #5B5BD6; }
.action-edit:hover { background: rgba(91,91,214,0.08); color: #4744b4; }
.action-sync { position: relative; }
.action-sync:hover { background: rgba(91,91,214,0.08); color: #5B5BD6; }

/* Sync-Status-Badge oben rechts */
.sync-badge {
  position: absolute; top: 3px; right: 3px;
  width: 11px; height: 11px; border-radius: 50%;
  background: #2E7D32; border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
  display: flex; align-items: center; justify-content: center;
}
/* Check mit SVG-Maske: kleiner weißer Haken */
.sync-synced .sync-badge {
  background: #2E7D32;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 10'><path fill='none' stroke='white' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round' d='M2.3 5.4l1.6 1.6 3.8-3.8'/></svg>");
  background-repeat: no-repeat; background-position: center;
  background-size: 9px 9px;
}
.sync-dirty .sync-badge { background: #FB8C00; }
.sync-missing .sync-badge { background: #c5cbd3; }

.divider-soft { height: 1px; background: #eceef2; }

/* Status Options */
.status-option {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px; background: white; border: 1px solid #eceef2;
  border-radius: 12px; cursor: pointer; transition: all 0.15s;
}
.status-option:hover { border-color: #c5cbd3; }
.status-active { border-color: #0a1628; background: #fafbfc; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }

.status-dot {
  width: 20px; height: 20px; min-width: 20px; border-radius: 50%;
  border: 2px solid #c5cbd3; background: white;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.status-dot-on { background: #0a1628; border-color: #0a1628; }
.status-dot-square { border-radius: 5px; }

.combo-hint {
  font-size: 11px; color: #888; margin-left: 8px;
}

.status-icon {
  width: 32px; height: 32px; min-width: 32px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
}
.status-body { flex: 1; min-width: 0; }
.status-label { font-size: 13px; font-weight: 700; color: #0a1628; }
.status-desc { font-size: 11px; color: #888; margin-top: 1px; }

.info-banner {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 10px 12px; background: rgba(91,91,214,0.08); color: #3A3AC0;
  border-radius: 10px; font-size: 12px; font-weight: 500;
  line-height: 1.5;
}
.info-banner .v-icon { margin-top: 1px; }
.info-banner span { flex: 1; }

.status-disabled {
  cursor: not-allowed !important;
  opacity: 0.55;
  background: #fafbfc !important;
}
.status-disabled:hover { border-color: #eceef2 !important; }

/* Image Preview */
.image-preview {
  padding: 8px; background: #f3f4f6; border-radius: 12px;
  cursor: pointer; transition: all 0.15s;
}
.image-preview:hover { background: #e8eaed; }
.image-preview :deep(.v-img) { border-radius: 8px; overflow: hidden; }
.image-fallback {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  aspect-ratio: 1; background: white; border-radius: 8px;
}
.fallback-text { font-size: 12px; color: #888; margin-top: 6px; }

/* Buttons */
.submit-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 11px 18px;
  background: #0a1628; color: white; border: none; border-radius: 10px;
  font-size: 13px; font-weight: 700; cursor: pointer;
  transition: background 0.15s; font-family: inherit;
}
.submit-btn:hover:not(:disabled) { background: #1d3457; }
.submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.submit-btn-purple { background: #7C4DFF; }
.submit-btn-purple:hover:not(:disabled) { background: #5E35B1; }
.submit-btn-blue { background: #1565C0; }
.submit-btn-blue:hover:not(:disabled) { background: #0D47A1; }
.submit-btn-orange { background: #E65100; }
.submit-btn-orange:hover:not(:disabled) { background: #BF360C; }

.bs {
  display: inline-flex; align-items: center; gap: 8px; width: 100%; justify-content: center;
  padding: 10px 14px; border: 1px dashed #c5cbd3; border-radius: 10px; background: #fafbfc;
  font-size: 13px; font-weight: 600; color: #555; cursor: pointer; transition: all 0.15s;
  font-family: inherit;
}
.bs:hover:not(:disabled) { border-color: #0a1628; color: #0a1628; background: white; }
.bs:disabled { opacity: 0.5; cursor: not-allowed; }

/* Alerts */
.alert-success {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; background: rgba(46,125,50,0.08); color: #2E7D32;
  border-radius: 10px; font-size: 12px; font-weight: 500;
}
.alert-error {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; background: rgba(211,47,47,0.08); color: #D32F2F;
  border-radius: 10px; font-size: 12px; font-weight: 500;
}

/* Floating Save */
.save-floating { position: fixed; bottom: 24px; right: 24px; z-index: 100; }
.save-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 14px 22px; background: #0a1628; color: white;
  border: none; border-radius: 12px; font-size: 14px; font-weight: 700;
  cursor: pointer; transition: all 0.15s;
  box-shadow: 0 8px 24px rgba(10,22,40,0.25); font-family: inherit;
}
.save-btn:hover:not(:disabled) { background: #1d3457; transform: translateY(-1px); }
.save-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.slide-fade-enter-active, .slide-fade-leave-active { transition: all 0.25s cubic-bezier(0.16,1,0.3,1); }
.slide-fade-enter-from, .slide-fade-leave-to { opacity: 0; transform: translateY(20px); }

/* Loading */
.loading-overlay {
  position: fixed; inset: 0; background: rgba(10,22,40,0.3);
  z-index: 9999; display: flex; align-items: center; justify-content: center;
  backdrop-filter: blur(4px);
}
.loading-card {
  background: white; border-radius: 16px; padding: 32px 48px;
  text-align: center; box-shadow: 0 12px 40px rgba(10,22,40,0.2);
}
.loading-text { font-size: 14px; color: #0a1628; font-weight: 600; }

.skel-block {
  height: 80px; border-radius: 10px;
  background: linear-gradient(90deg, #eceef2 25%, #f5f6f8 50%, #eceef2 75%);
  background-size: 200% 100%; animation: shimmer 1.5s infinite;
}

/* Guide */
.empty-guide {
  display: flex; flex-direction: column; align-items: center;
  padding: 20px 12px; text-align: center;
  background: #fafbfc; border: 1px dashed #c5cbd3; border-radius: 12px;
}
.empty-guide-title { font-size: 13px; font-weight: 700; color: #0a1628; margin-bottom: 4px; }
.empty-guide-desc { font-size: 11px; color: #888; line-height: 1.5; max-width: 260px; margin-bottom: 6px; }

/* ---- Anleitungen-Liste (Mehrfach-Verknüpfung) ---- */
.guide-card-head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; margin-bottom: 16px; flex-wrap: wrap;
}
.guide-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 20px; padding: 0 6px; margin-left: 8px;
  background: #eceef2; color: #0a1628;
  border-radius: 999px; font-size: 11px; font-weight: 700;
}
.guide-loading {
  display: flex; align-items: center; justify-content: center;
  padding: 28px 0;
}
.guide-list { display: flex; flex-direction: column; gap: 8px; }
.guide-row {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px;
  background: #fafbfc; border: 1px solid #eceef2; border-radius: 12px;
  cursor: pointer; transition: border-color 0.15s, background 0.15s;
}
.guide-row:hover { border-color: #c5cbd3; background: #fff; }
.guide-row-icon {
  width: 34px; height: 34px; border-radius: 9px; flex-shrink: 0;
  background: rgba(91,91,214,0.1);
  display: flex; align-items: center; justify-content: center;
}
.guide-row-main { flex: 1; min-width: 0; }
.guide-row-title {
  font-size: 13px; font-weight: 700; color: #0a1628;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.guide-row-preview {
  font-size: 11.5px; color: #888; margin-top: 2px; line-height: 1.4;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.guide-row-actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
.guide-act {
  width: 30px; height: 30px; border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent; border: none; color: #555;
  cursor: pointer; transition: background 0.15s, color 0.15s; font-family: inherit;
}
.guide-act:hover { background: #f0f2f5; color: #0a1628; }
.guide-act-danger:hover { background: rgba(211,47,47,0.08); color: #D32F2F; }

/* Pick-Dialog: bereits verknüpfte Einträge */
.pick-item-linked { opacity: 0.6; cursor: not-allowed !important; }
.pick-item-linked:hover { border-color: #eceef2 !important; background: white !important; }
.pick-linked-badge {
  flex-shrink: 0; padding: 3px 8px;
  background: rgba(46,125,50,0.1); color: #2E7D32;
  border-radius: 999px; font-size: 10.5px; font-weight: 700;
}

/* Sekundärer Text-Link unter dem Primary-Button */
.text-link-btn {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 8px; background: transparent; border: none;
  font-size: 11.5px; font-weight: 500; color: #5B5BD6;
  cursor: pointer; transition: color 0.15s; font-family: inherit;
}
.text-link-btn:hover { color: #4744b4; text-decoration: underline; }

.text-link-inline {
  color: #5B5BD6; font-weight: 600; text-decoration: none;
}
.text-link-inline:hover { text-decoration: underline; }

/* Pick-Guide Dialog */
.pick-guide-body { padding: 16px 20px; }
.pick-search {
  display: flex; align-items: center; gap: 8px;
  padding: 9px 12px;
  background: #fafbfc; border: 1px solid #eceef2; border-radius: 10px;
  margin-bottom: 12px;
}
.pick-search:focus-within { border-color: #0a1628; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.pick-search-input {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 13px; color: #0a1628; font-family: inherit;
}
.pick-search-clear { cursor: pointer; }

.pick-list {
  display: flex; flex-direction: column; gap: 4px;
  max-height: 360px; overflow-y: auto;
  padding-right: 2px;
}
.pick-item {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 8px 10px;
  background: white; border: 1px solid #eceef2; border-radius: 8px;
  cursor: pointer; transition: all 0.15s;
  text-align: left; font-family: inherit;
}
.pick-item:hover { border-color: #c5cbd3; background: #fafbfc; }
.pick-item.pick-item-active {
  border-color: #2E7D32;
  background: rgba(46,125,50,0.04);
  box-shadow: 0 0 0 3px rgba(46,125,50,0.06);
}
.pick-thumb {
  width: 36px; height: 36px; border-radius: 6px;
  background: #f0f2f5; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
}
.pick-thumb img { width: 100%; height: 100%; object-fit: cover; }
.pick-guide-icon {
  width: 36px; height: 36px; border-radius: 6px;
  background: rgba(91,91,214,0.08); flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.pick-preview {
  font-size: 11px; color: #9ca3af; line-height: 1.45;
  margin-top: 2px;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
}
.pick-meta {
  font-size: 10.5px; color: #5B5BD6; font-weight: 600;
  margin-top: 4px;
}
.pick-text { flex: 1; min-width: 0; }
.pick-title {
  font-size: 13px; font-weight: 600; color: #0a1628;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.pick-handle {
  font-size: 11px; color: #888;
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  margin-top: 1px;
}
.pick-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 40px 16px; text-align: center;
}

/* Guide Viewer */
.viewer-overlay {
  position: fixed; inset: 0; z-index: 9998;
  background: rgba(10,22,40,0.5); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  animation: fadeViewer 0.2s ease; padding: 24px;
}
@keyframes fadeViewer { from { opacity: 0; } to { opacity: 1; } }
.viewer-shell {
  width: 100%; max-width: 960px; max-height: 90vh;
  background: white; border-radius: 16px; overflow: hidden;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 64px rgba(10,22,40,0.25);
  animation: slideViewer 0.25s cubic-bezier(0.16,1,0.3,1);
}
@keyframes slideViewer { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
.viewer-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 24px; border-bottom: 1px solid #eceef2;
  flex-shrink: 0; gap: 16px; flex-wrap: wrap;
}
.header-icon {
  width: 38px; height: 38px; border-radius: 10px;
  background: rgba(10,22,40,0.06);
  display: flex; align-items: center; justify-content: center;
}
.header-title { font-size: 16px; font-weight: 700; color: #0a1628; }
.header-sub { font-size: 12px; color: #888; margin-top: 2px; }
.bo {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px; border: 1px solid #eceef2; border-radius: 9px;
  background: white; font-size: 12px; font-weight: 600; color: #555;
  cursor: pointer; transition: border-color 0.15s; font-family: inherit;
}
.bo:hover { border-color: #0a1628; }
.viewer-close {
  width: 36px; height: 36px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 8px; cursor: pointer; color: #888;
  transition: all 0.2s;
}
.viewer-close:hover { background: #f0f2f5; color: #0a1628; transform: rotate(90deg); }

.viewer-body {
  flex: 1; min-height: 0; overflow-y: auto;
  background: #fafbfc;
}
.viewer-content {
  padding: 32px 48px; color: #0a1628; line-height: 1.6;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
}
.viewer-content :deep(h1) { font-size: 28px; font-weight: 800; color: #0a1628; margin: 24px 0 12px; letter-spacing: -0.3px; }
.viewer-content :deep(h2) { font-size: 22px; font-weight: 700; color: #0a1628; margin: 20px 0 10px; }
.viewer-content :deep(h3) { font-size: 18px; font-weight: 700; color: #0a1628; margin: 16px 0 8px; }
.viewer-content :deep(p) { margin-bottom: 10px; }
.viewer-content :deep(a) { color: #5B5BD6; text-decoration: none; }
.viewer-content :deep(a:hover) { text-decoration: underline; }
.viewer-content :deep(img) { max-width: 100%; border-radius: 10px; display: block; margin: 14px auto; }
.viewer-content :deep(blockquote) {
  border-left: 3px solid #0a1628; padding: 6px 16px; margin: 14px 0;
  background: white; border-radius: 0 10px 10px 0; color: #555; font-style: italic;
}
.viewer-content :deep(pre) {
  background: #0a1628; color: #eceef2; padding: 16px; border-radius: 10px;
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
  font-size: 13px; line-height: 1.55; overflow-x: auto; margin: 14px 0;
}
.viewer-content :deep(ul), .viewer-content :deep(ol) { padding-left: 24px; margin-bottom: 10px; }

@media (max-width: 700px) {
  .viewer-content { padding: 20px 20px; }
}

.bs-danger {
  color: #D32F2F !important; border-color: rgba(211,47,47,0.3) !important;
  flex: 0 0 auto !important; width: 42px; padding: 0 !important;
}
.bs-danger:hover { background: rgba(211,47,47,0.06) !important; border-color: #D32F2F !important; }

/* ============ Sync Dialog ============ */
.sync-overlay {
  position: fixed; inset: 0; z-index: 9998;
  background: rgba(10,22,40,0.5); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 24px; animation: syncFade 0.2s ease;
}
@keyframes syncFade { from { opacity: 0; } to { opacity: 1; } }
.sync-shell {
  width: 100%; max-width: 1080px; max-height: 88vh;
  background: white; border-radius: 16px; overflow: hidden;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 64px rgba(10,22,40,0.25);
  animation: syncSlide 0.25s cubic-bezier(0.16,1,0.3,1);
}
@keyframes syncSlide { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

.sync-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 24px; border-bottom: 1px solid #eceef2;
  flex-shrink: 0; gap: 14px;
}
.sync-header-icon {
  width: 42px; height: 42px; min-width: 42px; border-radius: 11px;
  display: flex; align-items: center; justify-content: center;
}
.sync-icon-different { background: rgba(255,152,0,0.1); }
.sync-icon-not_in_repo { background: rgba(91,91,214,0.1); }
.sync-icon-identical { background: rgba(46,125,50,0.1); }
.sync-title { font-size: 16px; font-weight: 700; color: #0a1628; }
.sync-sub {
  font-size: 12px; color: #888; margin-top: 2px;
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
}
.sync-path { font-family: 'SFMono-Regular', Menlo, monospace; color: #0a1628; font-weight: 600; }
.sync-repo { color: #888; font-family: 'SFMono-Regular', Menlo, monospace; }

.sync-close {
  width: 34px; height: 34px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 8px; cursor: pointer; color: #888;
  transition: all 0.2s; flex-shrink: 0;
}
.sync-close:hover { background: #f0f2f5; color: #0a1628; transform: rotate(90deg); }

.sync-body { flex: 1; min-height: 0; overflow: auto; padding: 20px 24px; background: #fafbfc; }

.sync-state {
  display: flex; flex-direction: column; align-items: center;
  text-align: center; padding: 32px 16px;
}
.sync-state-title { font-size: 16px; font-weight: 700; color: #0a1628; margin-bottom: 6px; }
.sync-state-desc { font-size: 13px; color: #888; max-width: 520px; line-height: 1.55; }

.code-inline {
  display: inline-block;
  padding: 2px 8px; border-radius: 6px;
  background: #eceef2; color: #0a1628;
  font-family: 'SFMono-Regular', Menlo, monospace; font-size: 12px;
}

/* Diff */
.sync-diff-intro {
  padding: 10px 14px; background: rgba(255,152,0,0.08); color: #E65100;
  border-radius: 10px; font-size: 13px; margin-bottom: 14px;
}
.diff-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.diff-col {
  background: white; border: 1px solid #eceef2; border-radius: 10px; overflow: hidden;
  display: flex; flex-direction: column; min-height: 300px;
}
.diff-head {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; border-bottom: 1px solid #eceef2;
  font-size: 12px; font-weight: 700; color: #0a1628;
}
.diff-head-sam { background: #fafbfc; }
.diff-head-git { background: rgba(91,91,214,0.04); }
.diff-size { margin-left: auto; font-weight: 500; color: #888; font-family: 'SFMono-Regular', Menlo, monospace; }
.diff-pre {
  flex: 1; margin: 0; padding: 14px;
  background: white; color: #0a1628;
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 12px; line-height: 1.55;
  white-space: pre-wrap; word-break: break-word;
  overflow: auto; max-height: 420px;
}

.sync-footer {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 10px; padding: 14px 24px; border-top: 1px solid #eceef2;
  background: white;
}
.bo {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 18px; border: 1px solid #eceef2; border-radius: 10px;
  background: white; font-size: 13px; font-weight: 600; color: #555;
  cursor: pointer; transition: border-color 0.15s; font-family: inherit;
}
.bo:hover:not(:disabled) { border-color: #0a1628; }
.bo:disabled { opacity: 0.5; cursor: not-allowed; }

.bp {
  display: inline-flex; align-items: center; gap: 6px; min-width: 130px;
  padding: 10px 20px; background: #0a1628; color: white;
  border: none; border-radius: 10px; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: background 0.15s; justify-content: center;
  font-family: inherit;
}
.bp:hover:not(:disabled) { background: #1d3457; }
.bp:disabled { opacity: 0.5; cursor: not-allowed; }

.bp-indigo { background: #5B5BD6; }
.bp-indigo:hover:not(:disabled) { background: #4A4AB8; }
.bp-outline {
  background: white !important; color: #0a1628 !important;
  border: 1px solid #eceef2 !important;
}
.bp-outline:hover:not(:disabled) { border-color: #0a1628 !important; background: #fafbfc !important; }

@media (max-width: 800px) {
  .diff-grid { grid-template-columns: 1fr; }
  .sync-footer { flex-wrap: wrap; }
  .sync-footer button { flex: 1; min-width: 0; }
}
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

@media (max-width: 1100px) {
  .grid-split { grid-template-columns: 1fr; }
  .col-right { position: static; }
  .save-floating { left: 16px; right: 16px; }
  .save-btn { width: 100%; justify-content: center; }
}

@media (max-width: 700px) {
  .files-grid { grid-template-columns: 1fr; }
}
</style>
